import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { useLiveSubscription } from '../../contexts/LiveEventsContext'
import { useCoarsePointer, useSendOnEnter } from '../../hooks/use-send-on-enter'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { AssetPicker } from '../AssetPicker'
import { EntityPicture } from '../EntityPicture'
import { ContactPicker } from './ContactPicker'
import { displaySender, initialsOf } from './ConversationsPage'
import { DocumentPreviewCard } from '../documents/DocumentPreviewCard'
import { formatIso20022AddressShort } from '../../lib/format-address'
import { useFormatters } from '../../lib/format'
import { useConversationDraft } from './use-conversation-draft'
import { WhatsAppTemplatePicker, manualParamCount, useWhatsAppTemplates } from './WhatsAppTemplatePicker'
import { ComposerDocuments, ConfirmInternalDocumentsDialog, type SharedDocument } from './ComposerDocuments'
import type {
    Conversation,
    ConversationActivityEvent,
    ConversationMessage,
    ConversationStatus,
    Document,
    DroppedAttachment,
    MessageLocation,
    WhatsAppTemplate,
} from '../../api/schema'
import { useReadAcknowledgement, type RegisterReadNode } from './use-read-acknowledgement'
import { DetailSection } from './detail-section'
import { ConversationKnowledgeSection } from './ConversationKnowledgeSection'
import { KnowledgeSuggestionCard, SaveAsKnowledgeButton } from './MessageKnowledge'
import { EntityTagsSection } from '../tags/EntityTagsSection'
import { formatIssueReference } from '../../lib/issue-reference'
import { EmptyState } from '../ui/empty-state'
import { TriageSection, type ComposerApi, type PromoteSeed } from './TriageCard'
import {
    MEDIA_PLACEHOLDER_BODIES,
    MEDIA_RAW_TYPES,
    VOICE_PLACEHOLDER_BODY,
} from './message-placeholders'
import {
    FLAG_MANUAL_PRIORITY,
    FLAG_MANUAL_SUMMARY,
    FLAG_MANUAL_TITLE,
    PRIORITY_KEYS,
    PRIORITY_LEVELS,
    PRIORITY_TONES,
    SURVEY_TONES,
    surveyClass,
    threadHasFlag,
    threadPriority,
    threadTitle,
    threadTitleT,
} from './thread'
import { messageDelivery } from './delivery'
import { mdiAccountOutline, mdiCellphone, mdiCheck, mdiCheckCircle, mdiClose, mdiContentCopy, mdiEmailOutline, mdiEmoticonHappyOutline, mdiEmoticonNeutralOutline, mdiEmoticonSadOutline, mdiHome, mdiLoading, mdiMapMarker, mdiMessageOutline, mdiOpenInNew, mdiPaperclip, mdiPencil, mdiPlus, mdiReply, mdiRestore, mdiRobotOutline, mdiSend, mdiTicketOutline, mdiTrashCanOutline } from '@mdi/js'
import { SECTION_ICONS } from '../../lib/section-icons'
import { CreateApplicationButton } from '../rentalApplications/CreateApplicationButton'

/**
 * The thread view — the right pane of the queue, shaped like a mail client's
 * reading pane rather than a form:
 *
 *   - a header that states the subject, who is writing, and the state of the
 *     thread, with the three gestures that matter (reply, open a ticket,
 *     close);
 *   - the messages, each with a real sender and a real date, because "Received"
 *     / "Sent" tells a manager nothing they need;
 *   - the composer at the bottom, where a reply is typed in every mail client
 *     ever built;
 *   - a details column beside it: the contact, the assets, the tickets and the
 *     context — the facts the referential already holds about this asset,
 *     which is the honest phase-0 answer to "context" until the triage job
 *     retrieves more.
 *
 * Everything a manager corrects is a pick from a short list, because the
 * manager who saves time is the one triaging their queue during a site visit.
 */

const REQUEST_SUBTYPES = [
    'request_intervention',
    'request_information',
    'request_complaint',
    'request_administrative',
] as const

type RequestSubtype = (typeof REQUEST_SUBTYPES)[number]

const CHANNEL_ICON = { email: mdiEmailOutline, whatsapp: mdiCellphone, web: mdiMessageOutline } as const

/** Same tones as the ticket queue, so a ticket looks like a ticket everywhere. */
const ISSUE_STATUS_TONE: Record<string, string> = {
    open: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
    in_progress: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/20',
    closed: 'text-muted-foreground',
}

type LooseIssue = {
    uid?: string
    number?: number
    title?: string
    status?: string
    subtype?: string
    priority?: number
    due_date?: string | null
}

export function ConversationPanel({
    org,
    uid,
    focusedMessage,
    onFocusMessage,
    replyForIssue = null,
    onReplyForIssueHandled,
    onClose,
    onStatusChanged,
}: {
    org: string
    uid: string
    /** Deep-linked via `?message=` — the message the URL points at, if any. */
    focusedMessage: string | null
    /** Writes `?message=` back to the URL; `null` clears it. */
    onFocusMessage: (messageUid: string | null) => void
    /**
     * The closed ticket a manager came here to answer for, from the issue
     * page's "tell the people who asked" section (spec 2026-09-16-09). The
     * panel seeds the composer with its closing message once.
     */
    replyForIssue?: string | null
    /** Drops `?reply_for_issue=` from the URL once the seed has been acted on. */
    onReplyForIssueHandled?: () => void
    onClose: () => void
    /**
     * Reports a status change once the queue invalidation is queued, so the
     * page can decide whether the thread just left the folder on screen and,
     * if so, advance the selection to its neighbour (spec
     * 2026-09-10-06). The panel keeps owning the mutation — it also owns the
     * badge and the button state — the page owns the URL and the rows.
     */
    onStatusChanged?: (next: ConversationStatus) => void
}) {
    const { t, i18n } = useTranslation('conversations')
    const queryClient = useQueryClient()
    const composerRef = useRef<HTMLTextAreaElement | null>(null)
    // The pane the messages scroll in beside the details column. The
    // read-acknowledgement observer measures an over-tall bubble against it —
    // see `onScreen` in use-read-acknowledgement.
    const threadScrollRef = useRef<HTMLDivElement | null>(null)
    const [promoting, setPromoting] = useState(false)
    // What the promote form opens with. Non-null only on the triage card's
    // *Edit* path, where submitting records a review as `edited` rather than
    // promoting by hand (spec 2026-09-11-06 § 3).
    const [promoteSeed, setPromoteSeed] = useState<PromoteSeed | null>(null)
    // The composer publishes a tiny imperative handle here so the triage card can
    // ask "do you already hold text?" and push a draft across WITHOUT the
    // composer's text moving up into this component: the composer owns what is
    // typed in it, and spec 2026-09-11-07 keeps owning how it is autosaved.
    const composerApi = useRef<ComposerApi | null>(null)
    // « Rattacher une adresse » is offered in two places — the contact section
    // that owns it, and the composer's page-only hint, which is where a manager
    // actually notices the thread has no address (spec 2026-09-14-03 § A.5). A
    // monotonic counter rather than a boolean: pressing the hint again must
    // re-open a step the manager closed.
    const [mediumSignal, setMediumSignal] = useState(0)
    // The summary's editor is opened from two places — the pencil that sits on
    // the sender line while the thread has no summary, and the summary line
    // itself once it has one — so its state lives here rather than inside
    // either of them (spec 2026-09-09-07).
    const [editingSummary, setEditingSummary] = useState(false)
    const [editingTitle, setEditingTitle] = useState(false)
    // The closing message a manager came from the issue page to write (spec
    // 2026-09-16-09). Seeded ONCE per arrival, and only through the composer's
    // own handle — never by writing the draft row — so the seeded text is
    // autosaved and edited exactly like anything typed. `confirmSeed` holds it
    // while the manager decides what happens to a draft already in the box.
    const seededIssueRef = useRef<string | null>(null)
    const [confirmSeed, setConfirmSeed] = useState<string | null>(null)
    // Bumped when the composer has hydrated its fields from the autosaved
    // draft. The seed MUST wait for it: the composer publishes its handle from
    // a render where `body` is still empty, and a child's effects all run
    // before the parent's — so asking `hasText()` any earlier answers "no"
    // about a composer that is one render away from holding a draft, and the
    // seed would overwrite exactly what this spec promises to protect.
    const [composerHydrated, setComposerHydrated] = useState(0)

    const detail = $api.useQuery('get', '/api/v1/organizations/{orgCode}/conversations/{conversationUid}', {
        params: { path: { orgCode: org, conversationUid: uid } },
    })
    const conversation = detail.data as Conversation | undefined

    // The open thread's own scope: since migration 190 a message, a promotion
    // link, an about-set edit and a linked issue's state all publish a hint
    // carrying THIS conversation's uid, so the pane refreshes on its own while
    // a manager reads it.
    useLiveSubscription({ entity: 'conversations', uid })

    useEffect(() => {
        setPromoting(false)
        setPromoteSeed(null)
        setEditingSummary(false)
    }, [uid])

    // Arriving from a closed ticket: seed the composer with its closing
    // message. The ticket's reference and title come off the thread's OWN
    // `linked_issues` — already embedded in the detail payload — so no second
    // round trip, and a link that does not belong to this thread seeds
    // nothing. The parameter is dropped from the URL either way: a reload must
    // not re-seed over what is being typed.
    //
    // The composer publishes its handle from its own effect, and a child's
    // effects run before the parent's, so the handle is there by the time this
    // runs on the commit that mounts it.
    useEffect(() => {
        const conv = detail.data as Conversation | undefined
        if (!replyForIssue || !conv || composerHydrated === 0) return
        if (seededIssueRef.current === replyForIssue) return
        seededIssueRef.current = replyForIssue
        onReplyForIssueHandled?.()

        const link = (conv.linked_issues ?? []).find(
            (row) => String((row.issue as LooseIssue | undefined)?.uid ?? '') === replyForIssue,
        )
        const issue = link?.issue as LooseIssue | undefined
        if (!issue) return
        const reference = formatIssueReference(issue.number, i18n.language)
        const title = String(issue.title ?? '').trim()
        const body = title
            ? t('ticket_closed.seed', { reference, title })
            : t('ticket_closed.seed_no_title', { reference })

        // The triage card's rule (`:142`): a composer that already holds text
        // is asked before anything of the manager's is replaced.
        if (composerApi.current?.hasText()) {
            setConfirmSeed(body)

            return
        }
        composerApi.current?.applyDraft('', body, { rememberPrevious: true })
    }, [replyForIssue, detail.data, composerHydrated, onReplyForIssueHandled, i18n.language, t])

    const invalidate = () => {
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]

                return (
                    key.length >= 2 &&
                    key[0] === 'get' &&
                    typeof key[1] === 'string' &&
                    (key[1].includes('/conversations') || key[1].includes('/issues'))
                )
            },
        })
    }

    const patch = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/conversations/{conversationUid}', {
        onSuccess: (_data, variables) => {
            invalidate()
            const nextStatus = variables.body?.status
            if (nextStatus) onStatusChanged?.(nextStatus)
        },
    })

    if (detail.isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
            </div>
        )
    }
    if (!conversation) {
        return <div className="p-10 text-center text-sm text-muted-foreground">{t('panel.select_prompt')}</div>
    }

    const closed = conversation.status === 'closed'
    const channelIcon = CHANNEL_ICON[(conversation.channel ?? 'email') as keyof typeof CHANNEL_ICON] ?? mdiEmailOutline
    const sender = displaySender(conversation, t('contact.anonymous'))
    const identity = conversation.channel_identity ?? ''
    const contactName = conversation.contact?.name ? String(conversation.contact.name) : null
    const actions = (
        <RequestActions
            org={org}
            conversation={conversation}
            closed={closed}
            patch={patch}
            composerRef={composerRef}
            onPromote={() => {
                setPromoteSeed(null)
                setPromoting((v) => !v)
            }}
        />
    )

    return (
        <div className="flex flex-col @3xl:min-h-0 @3xl:h-full @container" data-testid="conversation-panel">
            {/* Header — subject, sender, state, the three gestures. The subject
                line carries the status badge and, once the pane is wide, the
                actions too: three stacked rows of chrome above a thread is two
                too many. */}
            <header className="px-4 md:px-5 py-3 border-b flex-shrink-0" data-testid="conversation-request-card">
                {/* One wrapping row, not two rendered branches: the actions are
                    mounted once, so `conversation-open-ticket` is one element
                    at every width. Below `@3xl` the identity block claims the
                    whole row (`basis-full`) and the actions wrap under it;
                    above it they share the subject's line: the block asks for
                    16rem only (`basis-0`), so a fourth action truncates the
                    subject instead of wrapping every action under it. */}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <div className="min-w-0 flex-1 basis-full @3xl:basis-0 @3xl:min-w-64">
                        <div className="flex items-center gap-2 min-w-0">
                            <ThreadTitleLine
                                org={org}
                                conversation={conversation}
                                patch={patch}
                                editing={editingTitle}
                                onEditingChange={setEditingTitle}
                            />
                            {!editingTitle && (
                                <Badge
                                    variant={closed ? 'outline' : 'secondary'}
                                    className="text-[10px] flex-shrink-0"
                                    data-testid="conversation-status-badge"
                                >
                                    {t(`status.${conversation.status ?? 'open'}`, { defaultValue: String(conversation.status ?? '') })}
                                </Badge>
                            )}
                            {!editingTitle && <PrioritySelect org={org} conversation={conversation} patch={patch} />}
                            {!editingTitle && conversation.survey && <SurveyScore survey={conversation.survey} />}
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={onClose}
                                aria-label={t('actions.back')}
                                title={t('actions.back')}
                                data-testid="conversation-close-panel"
                                className="lg:hidden flex-shrink-0 ml-auto"
                            >
                                <AppIcon path={mdiClose} size="sm" />
                            </Button>
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                            {/* The carrier, as an icon. `data-channel` carries
                                the code itself so a test can assert WHICH
                                carrier the thread is on — a thread moved onto
                                an address has to show it (spec
                                2026-09-14-03 § B). */}
                            <AppIcon
                                path={channelIcon}
                                size="xs"
                                className="flex-shrink-0"
                                aria-label={t(`channel.${conversation.channel ?? 'email'}`)}
                                data-testid="conversation-channel-icon"
                                data-channel={conversation.channel ?? 'email'}
                            />
                            <span className="truncate" data-testid="conversation-sender">
                                {contactName ? (
                                    <>
                                        <span className="text-foreground">{contactName}</span>
                                        {identity && !identity.startsWith('anonymous:') && <span> &lt;{identity}&gt;</span>}
                                    </>
                                ) : (
                                    sender
                                )}
                            </span>
                            <SummaryAddButton conversation={conversation} editing={editingSummary} onEdit={() => setEditingSummary(true)} />
                        </div>
                        <SummaryLine
                            org={org}
                            conversation={conversation}
                            patch={patch}
                            editing={editingSummary}
                            onEditingChange={setEditingSummary}
                        />
                    </div>
                    <div className="flex flex-wrap items-center gap-2">{actions}</div>
                </div>
            </header>

            {/* The details column needs the PANEL to be wide, not the window:
                with the app sidebar open, a 1280px window leaves this pane
                ~400px, where a side column would crush the messages. */}
            <div className="@3xl:flex-1 @3xl:min-h-0 grid grid-cols-1 @3xl:grid-cols-[minmax(0,1fr)_22rem] @5xl:grid-cols-[minmax(0,1fr)_26rem] @3xl:divide-x">
                {/* The thread and the composer. Beside the details column the
                    messages scroll on their own under a fixed composer; stacked,
                    everything flows and the pane scrolls as one. */}
                <div className="flex flex-col @3xl:min-h-0">
                    <div
                        className="@3xl:flex-1 @3xl:overflow-y-auto px-4 md:px-5 py-4 space-y-4"
                        data-testid="conversation-thread-scroll"
                        ref={threadScrollRef}
                    >
                        {promoting && (
                            <PromoteForm
                                org={org}
                                conversation={conversation}
                                seed={promoteSeed}
                                onDone={() => {
                                    setPromoting(false)
                                    setPromoteSeed(null)
                                    invalidate()
                                }}
                                onCancel={() => {
                                    setPromoting(false)
                                    setPromoteSeed(null)
                                }}
                            />
                        )}
                        <MessageList
                            org={org}
                            conversation={conversation}
                            focusedMessage={focusedMessage}
                            onFocusMessage={onFocusMessage}
                            scrollRootRef={threadScrollRef}
                        />
                    </div>
                    <Composer
                        org={org}
                        conversation={conversation}
                        onChanged={invalidate}
                        textareaRef={composerRef}
                        apiRef={composerApi}
                        onHydrated={() => setComposerHydrated((n) => n + 1)}
                        onAttachMedium={() => setMediumSignal((n) => n + 1)}
                    />
                </div>

                {/* Details — the contact, the assets, the tickets, the context.
                    Each section folds, and stays folded: an operator who never
                    reads Contact should be able to give its room to Context. */}
                <aside
                    className="border-t @3xl:border-t-0 @3xl:overflow-y-auto px-4 py-4 space-y-2 bg-muted/20"
                    data-testid="conversation-details"
                >
                    <ContactSection
                        org={org}
                        conversation={conversation}
                        onChanged={invalidate}
                        openMediumSignal={mediumSignal}
                    />
                    <AssetsSection org={org} conversation={conversation} onChanged={invalidate} />
                    <TicketsSection
                        org={org}
                        conversation={conversation}
                        onOpenTicket={() => {
                            setPromoteSeed(null)
                            setPromoting(true)
                        }}
                    />
                    <TriageSection
                        org={org}
                        conversation={conversation}
                        composerApi={composerApi}
                        onEditProposal={(seed) => {
                            setPromoteSeed(seed)
                            setPromoting(true)
                        }}
                        onChanged={invalidate}
                        onStatusChanged={onStatusChanged}
                    />
                    <ContextSection org={org} conversation={conversation} />
                    <ConversationKnowledgeSection
                        org={org}
                        assetUid={conversation.asset?.uid ? String(conversation.asset.uid) : undefined}
                        onInsert={(text) => composerApi.current?.insertText?.(text)}
                    />
                    <TagsSection org={org} conversation={conversation} />
                </aside>
            </div>

            {/* Seeding the closing message over what a manager has typed is a
                destructive act, so it is asked — in an AlertDialog, never a
                native confirm(). *Keep my text* leaves the composer alone. */}
            <AlertDialog open={confirmSeed !== null} onOpenChange={(open) => !open && setConfirmSeed(null)}>
                <AlertDialogContent data-testid="ticket-closed-seed-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('ticket_closed.replace_title')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('ticket_closed.replace_description')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="ticket-closed-seed-cancel">
                            {t('ticket_closed.replace_cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => {
                                if (confirmSeed !== null) {
                                    composerApi.current?.applyDraft('', confirmSeed, { rememberPrevious: true })
                                }
                                setConfirmSeed(null)
                            }}
                            data-testid="ticket-closed-seed-confirm"
                        >
                            {t('ticket_closed.replace_confirm')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}

/**
 * The thread's priority, on the issues' 1..5 scale (spec 2026-09-23-04).
 *
 * The level is ESTIMATED by the summary job every time the thread moves, until
 * a manager picks one — which sets `manual_priority` and stops the estimate
 * for good. The robot / hand mark on the trigger says which of the two the
 * level is, and a manual level offers the way back ("Estimate
 * automatically", PATCH `priority: 0`), the family's "clearing hands it back"
 * gesture. Every change of level shows up in the thread as a grey divider
 * (ActivityItem).
 */
function PrioritySelect({
    org,
    conversation,
    patch,
}: {
    org: string
    conversation: Conversation
    patch: { isPending: boolean; mutate: (input: never) => void }
}) {
    const { t } = useTranslation('conversations')
    const priority = threadPriority(conversation)
    const manual = threadHasFlag(conversation, FLAG_MANUAL_PRIORITY)
    const origin = manual ? t('priority.manual') : t('priority.automatic')
    const label = t('priority.select_label', {
        label: t('priority.label'),
        level: t(`priority.${PRIORITY_KEYS[priority]}`),
        origin,
    })

    return (
        <Select
            value={String(priority)}
            onValueChange={(value) =>
                patch.mutate({
                    params: { path: { orgCode: org, conversationUid: conversation.uid! } },
                    body: { priority: Number(value) },
                } as never)
            }
            disabled={patch.isPending}
        >
            <SelectTrigger
                className="h-6 px-2 py-0 gap-1.5 text-[11px] w-auto flex-shrink-0"
                aria-label={label}
                title={label}
                data-testid="conversation-priority-select"
                data-priority={priority}
                data-manual={manual ? 'true' : 'false'}
            >
                {/* The selected item renders its own dot and label. */}
                <SelectValue />
                <AppIcon
                    path={manual ? mdiAccountOutline : mdiRobotOutline}
                    size="xs"
                    tone="muted"
                    aria-hidden
                    data-testid={manual ? 'conversation-priority-manual' : 'conversation-priority-automatic'}
                />
            </SelectTrigger>
            <SelectContent>
                {PRIORITY_LEVELS.map((level) => (
                    <SelectItem key={level} value={String(level)} data-testid={`conversation-priority-option-${level}`}>
                        <span className="inline-flex items-center gap-2">
                            <span className={`inline-block w-2 h-2 rounded-full ${PRIORITY_TONES[level]}`} aria-hidden />
                            {t(`priority.${PRIORITY_KEYS[level]}`)}
                        </span>
                    </SelectItem>
                ))}
                {manual && (
                    <SelectItem value="0" data-testid="conversation-priority-option-automatic">
                        <span className="inline-flex items-center gap-2">
                            <AppIcon path={mdiRobotOutline} size="xs" aria-hidden />
                            {t('priority.hand_back')}
                        </span>
                    </SelectItem>
                )}
            </SelectContent>
        </Select>
    )
}

const SURVEY_ICONS = {
    detractor: mdiEmoticonSadOutline,
    passive: mdiEmoticonNeutralOutline,
    promoter: mdiEmoticonHappyOutline,
} as const

/**
 * The resident's closing-survey answer, beside the priority — read-only, since
 * only the resident's page writes it (spec 2026-09-11-01, decision 8). The
 * colour is the NPS class, for the manager's eye alone: the resident's page
 * shows the bare score. The comment, when there is one, is in the tooltip and
 * in the thread's `survey_answered` divider.
 */
function SurveyScore({ survey }: { survey: NonNullable<Conversation['survey']> }) {
    const { t } = useTranslation('conversations')
    const cls = surveyClass(survey.score)
    const label = t('survey.label', { score: survey.score, category: t(`survey.${cls}`) })
    const title = survey.comment ? `${label}\n“${survey.comment}”` : label

    return (
        <span
            className={`inline-flex items-center gap-1 h-6 px-2 rounded-md text-[11px] font-medium text-white tabular-nums flex-shrink-0 ${SURVEY_TONES[cls]}`}
            role="img"
            aria-label={label}
            title={title}
            data-testid="conversation-survey-score"
            data-score={survey.score}
            data-class={cls}
        >
            <AppIcon path={SURVEY_ICONS[cls]} size="xs" tone="inherit" aria-hidden />
            {survey.score}/10
        </span>
    )
}

/** The three gestures of the request card, rendered in whichever row fits. */
function RequestActions({
    org,
    conversation,
    closed,
    patch,
    composerRef,
    onPromote,
}: {
    org: string
    conversation: Conversation
    closed: boolean
    patch: { isPending: boolean; mutate: (input: never) => void }
    composerRef: React.MutableRefObject<HTMLTextAreaElement | null>
    onPromote: () => void
}) {
    const { t } = useTranslation('conversations')

    return (
        <>
            <Button
                        size="sm"
                        variant="default"
                        disabled={conversation.reachable === false}
                        onClick={() => composerRef.current?.focus()}
                        aria-label={t('actions.reply_action')}
                        title={conversation.reachable === false ? t('errors.no_recipient') : t('actions.reply_action')}
                        data-testid="conversation-reply-focus"
                    >
                        <AppIcon path={mdiReply} size="xs" className="md:mr-1" />
                        <span className="hidden md:inline">{t('actions.reply_action')}</span>
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onPromote}
                        aria-label={t('actions.create_ticket')}
                        title={t('actions.create_ticket')}
                        data-testid="conversation-open-ticket"
                    >
                        <AppIcon path={mdiTicketOutline} size="xs" className="md:mr-1" />
                        <span className="hidden md:inline">{t('actions.create_ticket')}</span>
                    </Button>
                    <CreateApplicationButton org={org} conversationUid={conversation.uid!} />
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={patch.isPending}
                        onClick={() =>
                            patch.mutate({
                                params: { path: { orgCode: org, conversationUid: conversation.uid! } },
                                body: { status: closed ? 'open' : 'closed' },
                            } as never)
                        }
                        aria-label={closed ? t('actions.reopen') : t('actions.close')}
                        title={closed ? t('actions.reopen') : t('actions.close')}
                        data-testid="conversation-toggle-status"
                    >
                        {closed ? <AppIcon path={mdiRestore} size="xs" className="md:mr-1" /> : <AppIcon path={mdiCheckCircle} size="xs" className="md:mr-1" />}
                        <span className="hidden md:inline">{closed ? t('actions.reopen') : t('actions.close')}</span>
                    </Button>
        </>
    )
}

/**
 * The thread's TITLE, click-to-edit (spec 2026-09-13-02).
 *
 * It is the resolution rule made visible: the manager's title, else the
 * generated one, else the carrier's subject, else the placeholder — all four
 * through `threadTitle`, so this `<h2>` says exactly what the queue row, the
 * breadcrumb and the resident's own page say.
 *
 * Editing mirrors the summary block right underneath: pencil affordance on the
 * text itself, Enter saves, Escape cancels, and EMPTYING the field hands the
 * thread back to the job — a WhatsApp thread gets a generated title on its
 * next run, an e-mail thread falls back to its Subject. The `manual` badge is
 * there for the same reason as the summary's: a manager has to be able to see
 * at a glance that this name stopped following the thread.
 *
 * Never rendered empty and never hidden: unlike the summary, every thread has
 * a name, even if it is only the placeholder.
 */
function ThreadTitleLine({
    org,
    conversation,
    patch,
    editing,
    onEditingChange,
}: {
    org: string
    conversation: Conversation
    patch: { isPending: boolean; mutate: (input: never) => void }
    editing: boolean
    onEditingChange: (editing: boolean) => void
}) {
    const { t } = useTranslation('conversations')
    // The STORED title, not the resolved one: the input must not seed itself
    // with the carrier's subject, or saving it unchanged would silently turn
    // the e-mail's own header into a manual title.
    const stored = (conversation.title ?? '').trim()
    const manual = threadHasFlag(conversation, FLAG_MANUAL_TITLE)
    const [draft, setDraft] = useState(stored)

    // The job can land a generated title while the pane is open; a closed
    // editor must follow it rather than keep the value it opened on.
    useEffect(() => {
        if (!editing) setDraft(stored)
    }, [stored, editing])

    const save = () => {
        patch.mutate({
            params: { path: { orgCode: org, conversationUid: conversation.uid! } },
            // The empty string is meaningful: it clears the title and hands
            // the thread back to automatic generation.
            body: { title: draft.trim() },
        } as never)
        onEditingChange(false)
    }
    const cancel = () => {
        setDraft(stored)
        onEditingChange(false)
    }

    if (editing) {
        return (
            <div className="min-w-0 flex-1 space-y-1.5">
                <Input
                    autoFocus
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            save()
                        }
                        if (e.key === 'Escape') {
                            e.preventDefault()
                            cancel()
                        }
                    }}
                    placeholder={t('title_edit.placeholder')}
                    className="h-8 text-base font-semibold"
                    data-testid="conversation-title-input"
                />
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" onClick={save} disabled={patch.isPending} data-testid="conversation-title-save">
                        <AppIcon path={mdiCheck} size="xs" className="mr-1" />
                        {t('title_edit.save')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancel} data-testid="conversation-title-cancel">
                        {t('title_edit.cancel')}
                    </Button>
                    <span className="text-[11px] text-muted-foreground">{t('title_edit.clear_hint')}</span>
                </div>
            </div>
        )
    }

    return (
        <>
            <h2
                className="text-base md:text-lg font-semibold text-foreground leading-tight truncate min-w-0"
                data-testid="conversation-title"
            >
                {threadTitleT(conversation, t)}
            </h2>
            <Button
                variant="ghost"
                size="icon"
                onClick={() => onEditingChange(true)}
                aria-label={t('title_edit.edit')}
                title={t('title_edit.edit')}
                data-testid="conversation-title-edit"
                className="h-6 w-6 flex-shrink-0 text-muted-foreground hover:text-foreground"
            >
                <AppIcon path={mdiPencil} size="xs" />
            </Button>
            {manual && (
                <Badge
                    variant="outline"
                    className="text-[10px] flex-shrink-0"
                    title={t('title_edit.clear_hint')}
                    data-testid="conversation-title-manual"
                >
                    {t('title_edit.manual')}
                </Badge>
            )}
        </>
    )
}

/**
 * The thread's summary, under the subject: what the request is about and where
 * it stands (spec 2026-09-09-07).
 *
 * It is generated, not typed — so the line is normally read-only prose, and the
 * click-to-edit exists for the case the generation got it wrong. Writing one by
 * hand sets `manual_summary` and stops generation for good, which is why the
 * badge is there: a manager has to be able to see, at a glance, that this
 * sentence stopped following the thread. Emptying the field hands the row back
 * to the job — the same gesture as clearing an auto-named room's name.
 *
 * With no summary it renders NOTHING: a thread whose summary has not been
 * generated yet (or a whole deployment with no LLM key) must not grow a row of
 * placeholder text under every subject — and must not push the header's action
 * row down either, which is what a hidden-but-present line would do. The
 * gesture that writes the first one lives on the sender line instead, as
 * SummaryAddButton.
 */
function SummaryLine({
    org,
    conversation,
    patch,
    editing,
    onEditingChange,
}: {
    org: string
    conversation: Conversation
    patch: { isPending: boolean; mutate: (input: never) => void }
    editing: boolean
    onEditingChange: (editing: boolean) => void
}) {
    const { t } = useTranslation('conversations')
    const summary = (conversation.summary ?? '').trim()
    const manual = threadHasFlag(conversation, FLAG_MANUAL_SUMMARY)
    const [draft, setDraft] = useState(summary)

    // The job can land a new summary while the pane is open; a closed editor
    // must follow it rather than keep showing the sentence it opened on.
    useEffect(() => {
        if (!editing) setDraft(summary)
    }, [summary, editing])

    const save = () => {
        patch.mutate({
            params: { path: { orgCode: org, conversationUid: conversation.uid! } },
            // The empty string is meaningful: it clears the summary and asks
            // for a generated one again.
            body: { summary: draft.trim() },
        } as never)
        onEditingChange(false)
    }
    const cancel = () => {
        setDraft(summary)
        onEditingChange(false)
    }

    if (editing) {
        return (
            <div className="mt-1.5 space-y-1.5">
                <Textarea
                    autoFocus
                    rows={2}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            save()
                        }
                        if (e.key === 'Escape') {
                            e.preventDefault()
                            cancel()
                        }
                    }}
                    placeholder={t('summary.placeholder')}
                    className="text-xs min-h-0"
                    data-testid="conversation-summary-input"
                />
                <div className="flex flex-wrap items-center gap-2">
                    <Button size="sm" onClick={save} disabled={patch.isPending} data-testid="conversation-summary-save">
                        <AppIcon path={mdiCheck} size="xs" className="mr-1" />
                        {t('summary.save')}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={cancel} data-testid="conversation-summary-cancel">
                        {t('summary.cancel')}
                    </Button>
                    <span className="text-[11px] text-muted-foreground">{t('summary.clear_hint')}</span>
                </div>
            </div>
        )
    }

    if (!summary) return null

    return (
        <div className="mt-1 flex items-start gap-1.5 min-w-0">
            <button
                type="button"
                onClick={() => onEditingChange(true)}
                title={t('summary.edit')}
                className="text-xs text-muted-foreground text-left line-clamp-3 min-w-0 hover:text-foreground transition-colors"
                data-testid="conversation-summary"
            >
                {summary}
            </button>
            {manual && (
                <Badge
                    variant="outline"
                    className="text-[10px] flex-shrink-0"
                    title={t('summary.clear_hint')}
                    data-testid="conversation-summary-manual"
                >
                    {t('summary.manual')}
                </Badge>
            )}
        </div>
    )
}

/**
 * The gesture that writes the first summary, on the sender line so a thread
 * without one keeps exactly the header it had before this spec. It disappears
 * as soon as there is a summary — the summary line itself is then the target.
 */
function SummaryAddButton({
    conversation,
    editing,
    onEdit,
}: {
    conversation: Conversation
    editing: boolean
    onEdit: () => void
}) {
    const { t } = useTranslation('conversations')
    if (editing || (conversation.summary ?? '').trim() !== '') return null

    return (
        <Button
            variant="ghost"
            size="icon"
            className="h-4 w-4 flex-shrink-0 text-muted-foreground"
            onClick={onEdit}
            aria-label={t('summary.edit')}
            title={t('summary.edit')}
            data-testid="conversation-summary-add"
        >
            <AppIcon path={mdiPencil} size="xs" />
        </Button>
    )
}

/* ------------------------------------------------------------------------ */
/* Messages                                                                  */
/* ------------------------------------------------------------------------ */

function MessageList({
    org,
    conversation,
    focusedMessage,
    onFocusMessage,
    scrollRootRef,
}: {
    org: string
    conversation: Conversation
    /** The message named by `?message=`, if any — see `MessageItem`. */
    focusedMessage: string | null
    onFocusMessage: (messageUid: string | null) => void
    /** The scrolling pane the bubbles live in, for the read observer. */
    scrollRootRef?: RefObject<HTMLDivElement | null>
}) {
    const { t } = useTranslation('conversations')
    const messages = useMemo(() => (conversation.messages ?? []) as ConversationMessage[], [conversation.messages])
    const listRef = useRef<HTMLOListElement | null>(null)
    // The thread's activity (spec 2026-09-23-04) — what happened to it that is
    // not correspondence — interleaved with the messages by time. A message
    // wins a tie: the event it provoked reads after it.
    const timeline = useMemo(() => {
        const events = (conversation.activity ?? []) as ConversationActivityEvent[]
        const entries: TimelineEntry[] = [
            ...messages.map((message) => ({ kind: 'message' as const, at: message.received_at ?? message.created_at ?? '', message })),
            ...events.map((event) => ({ kind: 'activity' as const, at: event.created_at ?? '', event })),
        ]

        return entries
            .map((entry, index) => ({ entry, index, time: Date.parse(entry.at) || 0 }))
            .sort((a, b) => a.time - b.time || (a.entry.kind === b.entry.kind ? a.index - b.index : a.entry.kind === 'message' ? -1 : 1))
            .map(({ entry }) => entry)
    }, [messages, conversation.activity])

    // A read is what someone SAW, and the server no longer infers it from a
    // fetch (spec 2026-09-10-05). The hook watches the inbound-unread bubbles
    // and acks the ones that stayed on screen, in a visible and focused window,
    // long enough to be read — so the badge, the queue row and the sidebar
    // count only move for a message a human actually looked at.
    const permissions = (conversation.permissions ?? []) as string[]
    const registerRead = useReadAcknowledgement({
        org,
        uid: conversation.uid ?? '',
        messages,
        enabled: Boolean(conversation.uid) && permissions.includes('conversations.read'),
        scrollRootRef,
    })

    // Open on what is unread, the way a mail client does — the top of a long
    // thread is the one place nobody needs to be taken to. Once per
    // conversation: a live refresh must not yank the view out from under
    // someone who has scrolled somewhere else.
    //
    // A `?message=` deep link names the exact place to open on instead, so it
    // takes over from the unread heuristic entirely for that open — tracked by
    // its own ref, keyed on uid+message, so clicking a *different* message
    // later re-centers on it even though the conversation's own "already
    // scrolled" guard has long since fired, while a live refresh that leaves
    // the target unchanged does not re-scroll a reader who moved on.
    const scrolledForUid = useRef<string | null>(null)
    const scrolledForMessage = useRef<string | null>(null)
    // Where the thread opened, kept in view while the page settles around it:
    // the header above (counters arriving, a notice wrapping) can grow after
    // the scroll and shrink this pane, which would leave the opening target
    // cut off. Released at the reader's first gesture.
    const pinned = useRef<{ node: Element; block: ScrollLogicalPosition } | null>(null)
    const uid = conversation.uid ?? ''
    useEffect(() => {
        const root = scrollRootRef?.current
        if (!root) return
        const release = () => {
            pinned.current = null
        }
        const resettle = () => pinned.current?.node.scrollIntoView({ block: pinned.current.block })
        const observer = new ResizeObserver(resettle)
        observer.observe(root)
        const gestures = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const
        for (const g of gestures) root.addEventListener(g, release, { passive: true })
        return () => {
            observer.disconnect()
            for (const g of gestures) root.removeEventListener(g, release)
            pinned.current = null
        }
    }, [uid, scrollRootRef])
    useEffect(() => {
        if (!uid || messages.length === 0) return

        if (focusedMessage) {
            const messageKey = `${uid}:${focusedMessage}`
            if (scrolledForMessage.current !== messageKey) {
                const node = listRef.current?.querySelector(`[data-testid="conversation-message-${focusedMessage}"]`)
                if (node) {
                    scrolledForMessage.current = messageKey
                    // The deep link decided where to open; the unread
                    // heuristic below must not also run for this uid.
                    scrolledForUid.current = uid
                    node.scrollIntoView({ block: 'center' })
                    pinned.current = { node, block: 'center' }
                    return
                }
                // A message uid that is not (or no longer) in this thread:
                // ignore it, no error, and fall through to the default below.
            } else {
                return
            }
        }

        if (scrolledForUid.current === uid) return
        scrolledForUid.current = uid
        const lastUnread = [...messages].reverse().find((m) => m.direction === 'inbound' && !m.read_at)
        const target = lastUnread ?? messages[messages.length - 1]
        const node = listRef.current?.querySelector(`[data-testid="conversation-message-${target.uid}"]`)
        // `nearest`, not `start`: the scrolling region is the messages pane
        // beside the details column but the page itself once they stack, and
        // `start` there would push the subject and the actions off the top of
        // a phone to reach a message that was already on screen.
        if (node) {
            node.scrollIntoView({ block: 'nearest' })
            pinned.current = { node, block: 'nearest' }
        }
    }, [uid, messages, focusedMessage])

    if (messages.length === 0) {
        // A thread a manager just opened from "write to a contact" (spec
        // 2026-09-16-10) — the dialog selected the contact, the site and the
        // subject, and the first message is written in the composer below. Say
        // so, and say what it costs: the thread is out of the queue until it
        // carries something. Its activity (a priority set before the first
        // message) still shows under the notice.
        return (
            <>
                <EmptyState
                    variant="inline"
                    icon={SECTION_ICONS.conversations}
                    title={t('panel.no_messages')}
                    description={t('panel.no_messages_hint')}
                    data-testid="conversation-no-messages"
                />
                {timeline.length > 0 && (
                    <ol className="space-y-3" data-testid="conversation-messages" ref={listRef}>
                        {timeline.map((entry) =>
                            entry.kind === 'activity' ? <ActivityItem key={entry.event.uid} event={entry.event} /> : null,
                        )}
                    </ol>
                )}
            </>
        )
    }

    return (
        <ol className="space-y-3" data-testid="conversation-messages" ref={listRef}>
            {timeline.map((entry) =>
                entry.kind === 'activity' ? (
                    <ActivityItem key={entry.event.uid} event={entry.event} />
                ) : (
                    <MessageItem
                        key={entry.message.uid}
                        org={org}
                        conversation={conversation}
                        message={entry.message}
                        focused={Boolean(entry.message.uid) && entry.message.uid === focusedMessage}
                        onFocus={onFocusMessage}
                        registerRead={registerRead}
                    />
                ),
            )}
        </ol>
    )
}

/**
 * What an activity event says, per kind — `null` for a kind this console does
 * not render yet. The one place a new kind is taught to the UI.
 */
function describeActivity(
    event: ConversationActivityEvent,
    t: (key: string, options?: Record<string, unknown>) => string,
): { text: string; tone: string | null; detail?: string } | null {
    switch (event.kind) {
        case 'priority_changed': {
            const payload = (event.payload ?? {}) as { from?: number; to?: number; origin?: string }
            const from = threadPriority({ priority: payload.from })
            const to = threadPriority({ priority: payload.to })
            const labels = { from: t(`priority.${PRIORITY_KEYS[from]}`), to: t(`priority.${PRIORITY_KEYS[to]}`) }
            const actor = (event.actor as { name?: string; email?: string } | null | undefined) ?? null
            const actorName = actor?.name || actor?.email || ''
            if (payload.origin === 'automatic') {
                return { text: t('activity.priority_changed_automatic', labels), tone: PRIORITY_TONES[to] }
            }
            if (actorName) {
                return { text: t('activity.priority_changed_by', { ...labels, actor: actorName }), tone: PRIORITY_TONES[to] }
            }

            return { text: t('activity.priority_changed_manual', labels), tone: PRIORITY_TONES[to] }
        }
        case 'survey_answered': {
            const payload = (event.payload ?? {}) as { score?: number; previous_score?: number; comment?: string }
            if (typeof payload.score !== 'number') return null
            const tone = SURVEY_TONES[surveyClass(payload.score)]
            const text =
                typeof payload.previous_score === 'number'
                    ? t('activity.survey_changed', { from: payload.previous_score, to: payload.score })
                    : t('activity.survey_answered', { score: payload.score })

            return { text, tone, detail: payload.comment ? `“${payload.comment}”` : undefined }
        }
        case 'document_shared': {
            // Who sent which file to the resident (spec 2026-10-10-05).
            const payload = (event.payload ?? {}) as { document_name?: string; visibility?: string }
            const actor = (event.actor as { name?: string; email?: string } | null | undefined) ?? null
            const actorName = actor?.name || actor?.email || ''
            const name = payload.document_name || '—'
            const text = actorName
                ? t('activity.document_shared_by', { actor: actorName, name })
                : t('activity.document_shared', { name })

            return {
                text,
                tone: null,
                detail: payload.visibility === 'internal' ? t('activity.document_shared_internal') : undefined,
            }
        }
        default:
            return null
    }
}

/** One row of the thread: a message, or an activity event between messages. */
type TimelineEntry =
    | { kind: 'message'; at: string; message: ConversationMessage }
    | { kind: 'activity'; at: string; event: ConversationActivityEvent }

/**
 * One activity event, rendered as a grey divider between the messages (spec
 * 2026-09-23-04). It is NOT a message — nothing here was sent to or by the
 * resident, which is why it has no bubble, no sender and no read state.
 *
 * The log is generic: `kind` picks the renderer. A kind this console does not
 * know renders NOTHING, so the backend can start recording a new kind before
 * its renderer ships. Adding one is a branch in describeActivity.
 */
function ActivityItem({ event }: { event: ConversationActivityEvent }) {
    const { t } = useTranslation('conversations')
    const { formatDate } = useFormatters()
    const described = describeActivity(event, t)
    if (!described) return null
    const { text, tone, detail } = described
    const when = event.created_at ? formatDate(event.created_at, { dateStyle: 'medium', timeStyle: 'short' }) : ''

    return (
        <li
            className="flex items-center gap-3 text-[11px] text-muted-foreground"
            data-testid={`conversation-activity-${event.uid}`}
            data-kind={event.kind}
        >
            <span className="h-px flex-1 bg-border" aria-hidden />
            <span className="flex flex-col items-center gap-0.5 min-w-0">
                <span className="inline-flex items-center gap-1.5 text-center min-w-0" data-testid="conversation-activity-text">
                    {tone && <span className={`inline-block w-2 h-2 rounded-full flex-shrink-0 ${tone}`} aria-hidden />}
                    <span>{text}</span>
                    {when && <span aria-hidden>·</span>}
                    {when && <span className="whitespace-nowrap">{when}</span>}
                </span>
                {detail && (
                    <span className="italic text-center max-w-md break-words" data-testid="conversation-activity-detail">
                        {detail}
                    </span>
                )}
            </span>
            <span className="h-px flex-1 bg-border" aria-hidden />
        </li>
    )
}

function MessageItem({
    org,
    conversation,
    message,
    focused,
    onFocus,
    registerRead,
}: {
    org: string
    conversation: Conversation
    message: ConversationMessage
    /** Whether this is the message named by `?message=`. */
    focused: boolean
    onFocus: (messageUid: string | null) => void
    /** Hands this bubble to the read-acknowledgement observer. */
    registerRead: RegisterReadNode
}) {
    const { t } = useTranslation('conversations')
    const { formatDate } = useFormatters()
    const [linkCopied, setLinkCopied] = useState(false)
    const outbound = message.direction === 'outbound'

    // Only an INBOUND, still-unread bubble is worth watching. When it stops
    // being either — our own ack landed, or another manager's screen stamped it
    // — the callback's identity changes, React calls the previous one with null
    // and the observer drops the node.
    const watchRead = !outbound && !message.read_at && Boolean(message.uid)
    const readRef = useCallback(
        (node: HTMLLIElement | null) => {
            if (!message.uid) return
            registerRead(message.uid, watchRead ? node : null)
        },
        [message.uid, registerRead, watchRead],
    )

    // A real sender, not a direction. Inbound is the resident (their contact
    // record when the sender resolved, the address on the wire otherwise);
    // outbound is the manager who wrote it, or the team when it was the
    // automatic receipt.
    const author = (message as { author?: { name?: string; email?: string } }).author
    const contactName = conversation.contact?.name ? String(conversation.contact.name) : null
    const identity = conversation.channel_identity ?? ''
    const name = outbound
        ? author?.name || t('panel.team')
        : contactName || displaySender(conversation, t('contact.anonymous'))
    // The address beside the name — unless the address IS the name (an
    // unresolved sender), where printing it twice says nothing new.
    const rawAddress = outbound ? author?.email ?? '' : identity.startsWith('anonymous:') ? '' : identity
    const address = rawAddress === name ? '' : rawAddress
    const when = message.received_at ?? message.created_at
    const { status: delivery, error: deliveryError } = messageDelivery(message)
    // Full document rows since spec 2026-09-08-07 — before that the schema
    // typed them as opaque objects and the console could only count them.
    const attachments = (message.attachments ?? []) as Document[]
    const dropped = (message.dropped_attachments ?? []) as DroppedAttachment[]
    // A voice message (spec 2026-09-09-01): the recording is the trace and the
    // transcript catches up. While the body is still the server's placeholder,
    // the console says so and plays the audio — which is why an audio
    // attachment opens expanded rather than collapsed.
    const isVoice = message.raw_type === 'audio'
    const awaitingTranscript = isVoice && (message.body_text ?? '').trim() === VOICE_PLACEHOLDER_BODY
    // A media message whose bytes have not landed yet (spec 2026-09-09-12).
    // Read as a STATE, not as a stored string: no attachment and no dropped
    // entry on a media row means the fetch job is still on its way, and the
    // live-events invalidation refreshes the thread when it arrives.
    const mediaPending =
        message.direction === 'inbound' &&
        MEDIA_RAW_TYPES.has(String(message.raw_type ?? '')) &&
        attachments.length === 0 &&
        dropped.length === 0
    const pendingLabel = t(`panel.media_pending_${message.raw_type}`, { defaultValue: t('panel.media_pending') })
    // A stored sentinel is never printed: it is the server's marker, not the
    // resident's words. A caption IS the resident's words and always shows,
    // pending fetch or not.
    const bodyText = (message.body_text ?? '').trim()
    const showBody = bodyText !== '' && bodyText !== VOICE_PLACEHOLDER_BODY && !MEDIA_PLACEHOLDER_BODIES.has(bodyText)
    const location = (message as { location?: MessageLocation | null }).location ?? null
    // Built client-side from the coordinates: the console never fetches a tile
    // or announces the pin to a third party just to render a row.
    const mapsHref = location ? `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lng}` : null

    // Toggling: clicking the already-focused message's header clears the
    // deep link rather than re-asserting it, so the affordance also serves as
    // "stop pointing at this one".
    const toggleFocus = useCallback(() => {
        if (!message.uid) return
        onFocus(focused ? null : message.uid)
    }, [focused, message.uid, onFocus])

    const handleHeaderKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            // Only the row itself, not a keypress bubbling up from the
            // nested copy-link button — that button has its own click/Enter
            // handling and must not also toggle the focus.
            if (event.target !== event.currentTarget) return
            if (event.key !== 'Enter' && event.key !== ' ') return
            event.preventDefault()
            toggleFocus()
        },
        [toggleFocus],
    )

    // Only `conversation` and `message` travel: the sender's current filters
    // (status, channel, q…) are theirs, not the recipient's — the link must
    // land on the thread whatever folder the recipient's console defaults to.
    const copyLink = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation()
            if (!conversation.uid || !message.uid) return
            const url = `${window.location.origin}/${org}/conversations?conversation=${conversation.uid}&message=${message.uid}`
            if (typeof navigator !== 'undefined' && navigator.clipboard) {
                void navigator.clipboard.writeText(url).then(() => {
                    setLinkCopied(true)
                    setTimeout(() => setLinkCopied(false), 1500)
                })
            }
        },
        [conversation.uid, message.uid, org],
    )

    return (
        <li
            ref={readRef}
            id={`message-${message.uid}`}
            className={`group/message rounded-lg border px-3.5 py-3 ${outbound ? 'bg-primary/5 border-primary/15' : 'bg-card'} ${
                focused ? 'ring-2 ring-primary/40' : ''
            }`}
            data-testid={`conversation-message-${message.uid}`}
            data-focused={focused ? 'true' : undefined}
            // Read straight off the server's `read_at`, so an e2e test can see
            // which bubbles the acknowledgement has actually moved.
            data-unread={watchRead ? 'true' : undefined}
        >
            <div className="flex items-start gap-3">
                <div
                    className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                        outbound ? 'bg-primary/15 text-primary' : 'bg-muted text-foreground'
                    }`}
                    aria-hidden
                >
                    {initialsOf(name)}
                </div>
                <div className="min-w-0 flex-1">
                    <div
                        className="flex items-baseline justify-between gap-2 min-w-0 cursor-pointer rounded hover:bg-muted/40 -mx-1 px-1"
                        role="button"
                        tabIndex={0}
                        onClick={toggleFocus}
                        onKeyDown={handleHeaderKeyDown}
                        aria-pressed={focused}
                        title={t(focused ? 'panel.message_unfocus' : 'panel.message_focus')}
                        data-testid="conversation-message-header"
                    >
                        <div className="min-w-0 truncate">
                            <span className="text-sm font-medium text-foreground" data-testid="message-sender">
                                {name}
                            </span>
                            {address && <span className="ml-1.5 text-xs text-muted-foreground">&lt;{address}&gt;</span>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                            <time
                                className="text-[11px] text-muted-foreground"
                                dateTime={when ?? undefined}
                                data-testid="message-date"
                            >
                                {when ? formatDate(when, { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                            </time>
                            <button
                                type="button"
                                onClick={copyLink}
                                className="opacity-0 group-hover/message:opacity-100 group-focus-within/message:opacity-100 focus:opacity-100 rounded p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-opacity"
                                aria-label={t('panel.copy_message_link')}
                                title={t('panel.copy_message_link')}
                                data-testid="conversation-message-copy-link"
                            >
                                {linkCopied ? <AppIcon path={mdiCheck} size="xs" /> : <AppIcon path={mdiContentCopy} size="xs" />}
                            </button>
                            {outbound && message.uid && (
                                <SaveAsKnowledgeButton org={org} conversation={conversation} messageUid={String(message.uid)} />
                            )}
                            {linkCopied && (
                                <span className="sr-only" data-testid="conversation-message-copy-link-copied">
                                    {t('panel.message_link_copied')}
                                </span>
                            )}
                        </div>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                        <span>{outbound ? t('panel.outbound') : t('panel.inbound')}</span>
                        {outbound && delivery && (
                            <Badge variant="outline" className="text-[10px]" title={deliveryError}>
                                {t(`panel.delivery_${delivery}`, { defaultValue: delivery })}
                            </Badge>
                        )}
                        {message.auto_submitted && (
                            <span className="inline-flex items-center gap-1">
                                <AppIcon path={mdiRobotOutline} size="xs" />
                                {t('panel.auto_reply')}
                            </span>
                        )}
                        {attachments.length > 0 && (
                            <span className="inline-flex items-center gap-1">
                                <AppIcon path={mdiPaperclip} size="xs" />
                                {t('panel.attachments', { count: attachments.length })}
                            </span>
                        )}
                    </div>
                    {outbound && deliveryError && (
                        <p className="mt-0.5 text-[10px] break-words text-destructive" data-testid="conversation-message-delivery-error">
                            {deliveryError}
                        </p>
                    )}
                    {showBody && (
                        // A reading measure, not the card's width: a paragraph
                        // set across 900 px is a paragraph nobody scans. The
                        // card stays full width so the sender/date line spans.
                        <p
                            className="mt-2 max-w-prose text-sm text-foreground whitespace-pre-wrap break-words"
                            data-testid="message-body"
                        >
                            {message.body_text}
                        </p>
                    )}
                    {mediaPending ? (
                        // "Being retrieved", never "unsupported": the content
                        // IS coming, and the manager has no WhatsApp to open.
                        <p
                            className="mt-2 flex items-center gap-1.5 text-sm italic text-muted-foreground"
                            data-testid="message-media-pending"
                        >
                            <AppIcon path={mdiLoading} size="xs" className="motion-safe:animate-spin" />
                            {pendingLabel}
                        </p>
                    ) : awaitingTranscript ? (
                        // A voice message whose transcript has not landed (or
                        // never will) is not a message with a body that reads
                        // like a stored sentinel: say what it is, and play the
                        // audio below (spec 2026-09-09-01).
                        <p className="mt-2 text-sm italic text-muted-foreground" data-testid="message-voice-no-transcript">
                            {t('panel.voice_no_transcript')}
                        </p>
                    ) : null}
                    {location && mapsHref && (
                        // A pin is coordinates, not a sentence about a place:
                        // the row opens the exact spot the resident stood on.
                        <a
                            href={mapsHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-2 inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs text-foreground hover:bg-muted"
                            data-testid="message-location"
                            title={t('panel.location_open_in_maps')}
                        >
                            <AppIcon path={mdiMapMarker} size="xs" />
                            <span className="truncate max-w-[22rem]">
                                {location.name || location.address || t('panel.location_pin')}
                            </span>
                            <span className="text-muted-foreground">
                                {location.lat.toFixed(5)}, {location.lng.toFixed(5)}
                            </span>
                            <AppIcon path={mdiOpenInNew} size="xs" />
                        </a>
                    )}
                    {message.truncated && (
                        <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">{t('panel.truncated')}</p>
                    )}
                    {attachments.length > 0 && (
                        <div className="mt-2 space-y-1.5" data-testid="conversation-attachments">
                            {attachments.map((attachment) => (
                                <DocumentPreviewCard
                                    key={attachment.uid}
                                    org={org}
                                    documentUid={String(attachment.uid)}
                                    document={attachment}
                                    defaultOpen={String(attachment.file_mime_type ?? '').startsWith('audio/')}
                                    testidPrefix="conversation-attachment"
                                />
                            ))}
                        </div>
                    )}
                    {dropped.length > 0 && (
                        <p
                            className="mt-2 text-xs text-muted-foreground"
                            data-testid="conversation-dropped-attachments"
                        >
                            {t('panel.dropped_attachments_intro')}{' '}
                            {dropped
                                .map((entry) =>
                                    t('panel.dropped_attachment_entry', {
                                        name: entry.name,
                                        reason: t(`panel.dropped_reason_${entry.reason}`, {
                                            defaultValue: t('panel.dropped_reason_unknown'),
                                        }),
                                    }),
                                )
                                .join(' · ')}
                        </p>
                    )}
                    {outbound &&
                        (conversation.knowledge_proposals ?? [])
                            .filter((p) => p.message_uid === message.uid && p.origin === 'conversation_reply')
                            .map((p) => <KnowledgeSuggestionCard key={p.uid} org={org} proposalUid={p.uid} title={p.title} />)}
                </div>
            </div>
        </li>
    )
}

/* ------------------------------------------------------------------------ */
/* Composer                                                                  */
/* ------------------------------------------------------------------------ */

function Composer({
    org,
    conversation,
    onChanged,
    textareaRef,
    apiRef,
    onHydrated,
    onAttachMedium,
}: {
    org: string
    conversation: Conversation
    onChanged: () => void
    /** Opens the contact section's « Rattacher une adresse » step. */
    onAttachMedium?: () => void
    textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>
    /**
     * Where the composer publishes what the triage card is allowed to do to it
     * (spec 2026-09-11-06 § 3): ask whether it holds text, and take a draft.
     *
     * A handle rather than lifted state, for two reasons that pull the same way:
     * the composer keeps owning what is typed in it — which is what lets spec
     * 2026-09-11-07 autosave it without this component knowing — and the card
     * needs the CURRENT text to decide whether replacing it has to be confirmed,
     * which a prop passed downwards cannot give it.
     */
    apiRef?: React.MutableRefObject<ComposerApi | null>
    /**
     * Fired once the fields have been seeded from the autosaved draft, so the
     * panel knows the handle it is about to question is answering about the
     * real text rather than about an empty first render (spec 2026-09-16-09).
     */
    onHydrated?: () => void
}) {
    const { t } = useTranslation('conversations')
    const { formatDate } = useFormatters()
    const [subject, setSubject] = useState('')
    const [body, setBody] = useState('')
    const [error, setError] = useState<string | null>(null)
    // What a pushed draft replaced, when the caller asked for it to be
    // remembered (spec 2026-09-16-09). The autosaved row has ALREADY taken it
    // — this is what makes that visible, and reversible, instead of leaving a
    // manager to wonder where their text went.
    const [previousDraft, setPreviousDraft] = useState<string | null>(null)
    // The triage whose draft *Use this draft* put in the composer; sent with
    // the reply so the server can tell a kept draft from a rewrite.
    const [draftTriageUid, setDraftTriageUid] = useState<string | null>(null)
    // The WhatsApp template reply (spec 2026-10-07-02): `useTemplate` is the
    // manager's "send a template instead" while the window is open; once it
    // has closed the picker is the only way to send. Plain state, never the
    // autosaved draft.
    const [useTemplate, setUseTemplate] = useState(false)
    const [template, setTemplate] = useState<WhatsAppTemplate | null>(null)
    const [templateParams, setTemplateParams] = useState<string[]>([])
    // Library documents going out with the reply (spec 2026-10-10-05), and the
    // "send an internal document anyway?" confirmation. Plain state, like the
    // template: never part of the autosaved draft.
    const [sharedDocs, setSharedDocs] = useState<SharedDocument[]>([])
    const [confirmingInternal, setConfirmingInternal] = useState(false)

    // Autosaved human draft (spec 2026-09-11-07): survives a thread switch, a
    // reload, or a session expiry. `hydrate` decides what to seed the fields
    // with — the caller's embedded draft, a newer localStorage copy, or the
    // untouched default — and must run from THIS effect so it wins over the
    // plain reset that used to live here.
    const draft = useConversationDraft({
        org,
        conversationUid: conversation.uid ?? '',
        embeddedDraft: conversation.draft,
        subject,
        body,
        // The cached conversation query must not go stale relative to what
        // was just autosaved, or switching away and back inside its 5-minute
        // stale time would re-hydrate from the pre-save copy.
        onSaved: onChanged,
    })

    // What the fields were last seeded with, for the re-seed further down.
    const seededRef = useRef<{ uid?: string; defaultSubject: string; subject: string; body: string } | null>(null)

    useEffect(() => {
        // A thread that has never carried a message is one a manager just
        // opened from "write to a contact" (spec 2026-09-16-10). Two things
        // follow from that, and both are about this being a FIRST message
        // rather than a reply: its subject line is the thread's own, with no
        // `Re:` in front of it — nothing has been received to answer — and the
        // manager came here to type, so the composer takes the caret. Focus is
        // never stolen on a thread they are reading.
        const firstMessage = (conversation.messages?.length ?? 0) === 0
        const subjectSeed = conversation.subject
            ? firstMessage
                ? conversation.subject
                : `Re: ${conversation.subject}`
            : ''
        const seed = draft.hydrate(subjectSeed)
        seededRef.current = { uid: conversation.uid, defaultSubject: subjectSeed, ...seed }
        setSubject(seed.subject)
        setBody(seed.body)
        setError(null)
        setPreviousDraft(null)
        setDraftTriageUid(null)
        setUseTemplate(false)
        setTemplate(null)
        setTemplateParams([])
        setSharedDocs([])
        setConfirmingInternal(false)
        onHydrated?.()
        if (firstMessage) textareaRef.current?.focus()
        // draft.hydrate is stable per (org, conversationUid, embeddedDraft) and
        // intentionally excluded here: including it would re-run this effect
        // on every draft.status change, re-seeding the fields out from under
        // whatever the manager is typing. `conversation.messages` is excluded
        // for the same reason — it changes on every refresh, and re-seeding on
        // an arriving message would eat what is being typed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation.uid, conversation.subject])

    // The fields were seeded from whatever copy of the conversation was on
    // hand when the thread opened — on a switch back, the cached one, which
    // can predate the draft just saved for it (the save drops the local
    // mirror once the server holds it). The refetch then brings the draft,
    // and this lays it in, as long as nothing has been typed since the seed.
    useEffect(() => {
        const seeded = seededRef.current
        if (!seeded || seeded.uid !== conversation.uid || !conversation.draft) return
        if (subject !== seeded.subject || body !== seeded.body) return
        const seed = draft.hydrate(seeded.defaultSubject)
        if (seed.subject === subject && seed.body === body) return
        seededRef.current = { ...seeded, ...seed }
        setSubject(seed.subject)
        setBody(seed.body)
        // Only a newer server draft is news here; the fields and the hook are
        // read as they stand.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conversation.draft?.updated_at])

    const reply = $api.useMutation('post', '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/reply', {
        onSuccess: () => {
            setBody('')
            setDraftTriageUid(null)
            setError(null)
            setTemplate(null)
            setTemplateParams([])
            setUseTemplate(false)
            setSharedDocs([])
            // The server already cleared the row in the same transaction as
            // the send; this drops the local mirror so it cannot resurrect
            // the sent text on the next open.
            draft.clearAfterSend()
            onChanged()
        },
        // The server's reason, never a stand-in: this used to relabel every
        // failure "a subject and a message are required", which sent people
        // hunting for a form problem when the real answer was "this resident
        // left no address". The body's `code` is what tells the cases apart.
        onError: (e: unknown) => {
            const res = e as { code?: string; title?: string; fields?: { message?: string }[] } | undefined
            switch (res?.code) {
                case 'NO_REACHABLE_ADDRESS':
                    setError(t('errors.no_recipient'))
                    break
                case 'OUTBOUND_CAPPED':
                    setError(t('errors.capped'))
                    break
                case 'VALIDATION_ERROR':
                    setError(res?.fields?.[0]?.message || t('errors.reply_required'))
                    break
                case 'WHATSAPP_UNAVAILABLE':
                    setError(t('errors.whatsapp_unavailable'))
                    break
                case 'WHATSAPP_WINDOW_CLOSED':
                    // The window closed while the composer still read it as
                    // open: the picker is the way forward.
                    setError(t('errors.whatsapp_window_closed'))
                    setUseTemplate(true)
                    break
                case 'DOCUMENT_INTERNAL':
                    // The audience changed since the pick: ask again.
                    setConfirmingInternal(true)
                    break
                case 'DOCUMENT_NOT_FOUND':
                    setError(t('share.not_found'))
                    break
                case 'WHATSAPP_SEND_FAILED':
                    setError(`${t('errors.whatsapp_send_failed')} ${res?.title ?? ''}`.trim())
                    break
                default:
                    setError(res?.title ? `${t('errors.reply_failed')} ${res.title}` : t('errors.reply_failed'))
            }
        },
    })

    // No address to send to: say so and do not offer a Send button that can
    // only fail. The server enforces the same rule; this is the honest version
    // of it. A `web` thread is never in this state — its public page is a
    // reply destination in its own right (spec 2026-09-14-03 § A).
    const unreachable = conversation.reachable === false
    // A page-only reply: the message is posted on the thread's page and
    // nothing leaves the platform, so there is no subject line to fill and
    // nobody to notify — said up front, next to the gesture that fixes it.
    //
    // Read off the server, never re-derived from `channel_identity` here: the
    // identity is stored as the reporter typed it, and only the backend's
    // `contactnorm.Email` decides whether it is an address. A regex that said
    // "no address" about `Jean <jean@example.com>` hid the subject field on a
    // thread whose send then failed for want of one.
    const pageOnly = conversation.page_only === true
    // WhatsApp (spec 2026-09-08-06): no subject line, and what may leave
    // depends on Meta's 24 h window — free text while it is open, only an
    // approved template once it has closed (spec 2026-10-07-02). Said up
    // front, and the composer offers only what can actually be sent.
    const isWhatsApp = conversation.channel === 'whatsapp'
    const windowUntil = conversation.whatsapp_window_until ? new Date(conversation.whatsapp_window_until) : null
    const windowOpen = windowUntil !== null && windowUntil.getTime() > Date.now()
    const templateMode = isWhatsApp && !unreachable && (!windowOpen || useTemplate)
    const catalogue = useWhatsAppTemplates(org, templateMode)
    // A WhatsApp thread started by a manager has no message: its first one is
    // the "continue on the web" template, so the picker opens on it (spec
    // 2026-10-26-04). Only when nothing is chosen yet.
    const continueName = catalogue.data?.continue_template
    const continueLanguage = catalogue.data?.continue_language
    const hasMessages = (conversation.messages?.length ?? 0) > 0
    useEffect(() => {
        if (!templateMode || hasMessages || template !== null || !catalogue.data) return
        const tpl = catalogue.data.templates.find((x) => x.name === continueName && x.language === continueLanguage)
        if (tpl) {
            setTemplate(tpl)
            setTemplateParams(Array.from({ length: manualParamCount(tpl, continueName) }, () => ''))
        }
    }, [templateMode, hasMessages, template, catalogue.data, continueName, continueLanguage])
    const templateReady =
        template !== null && templateParams.length === manualParamCount(template, continueName) && templateParams.every((p) => p.trim() !== '')
    const identity = conversation.channel_identity ?? ''
    const recipient = conversation.contact?.name
        ? `${String(conversation.contact.name)}${identity && !identity.startsWith('anonymous:') ? ` <${identity}>` : ''}`
        : identity.startsWith('anonymous:')
          ? t('contact.anonymous')
          : identity

    // Shared by the Send button and the Enter-to-send keyboard path so the
    // two cannot drift: same validation, same mutation, same error mapping.
    const noSubject = isWhatsApp || pageOnly
    // Documents ride on a template only when its button opens the page they
    // wait on.
    const canShareDocs = !unreachable && (!templateMode || template?.has_url_button === true)
    const sendReply = useCallback((confirmInternal: boolean) => {
        const docs = canShareDocs ? sharedDocs : []
        if (docs.some((d) => d.internal) && !confirmInternal) {
            setConfirmingInternal(true)

            return
        }
        const shared = docs.length > 0
            ? { attachment_document_uids: docs.map((d) => d.uid), confirm_internal_documents: confirmInternal }
            : {}
        if (templateMode) {
            if (!template || !templateReady) {
                setError(t('template.required'))

                return
            }
            reply.mutate({
                params: { path: { orgCode: org, conversationUid: conversation.uid! } },
                body: {
                    template_name: template.name,
                    template_language: template.language,
                    template_params: templateParams,
                    // A template with a link to the resident's page can carry
                    // a message that waits there.
                    ...(template.has_url_button && body.trim() ? { body } : {}),
                    ...shared,
                },
            })

            return
        }
        if (!body.trim() || (!noSubject && !subject.trim())) {
            setError(t('errors.reply_required'))

            return
        }
        reply.mutate({
            params: { path: { orgCode: org, conversationUid: conversation.uid! } },
            // WhatsApp has no subject line, and a page-only reply has no mail
            // to put one on — the server requires one only when a mail leaves.
            // The triage whose draft the text started from: the server
            // compares the two and records how much of the draft was kept
            // (spec 2026-11-01-01).
            body: {
                ...(noSubject ? { body } : { subject, body }),
                ...shared,
                ...(draftTriageUid ? { draft_triage_uid: draftTriageUid } : {}),
            },
        })
    }, [body, noSubject, subject, reply, org, conversation.uid, t, templateMode, template, templateReady, templateParams, canShareDocs, sharedDocs, draftTriageUid])
    const submit = useCallback(() => sendReply(false), [sendReply])

    // Re-published on every keystroke so `hasText` is never stale: the card asks
    // it at click time, and a handle built once would answer about an empty
    // composer forever.
    useEffect(() => {
        if (!apiRef) return
        apiRef.current = {
            hasText: () => body.trim() !== '',
            applyDraft: (draftSubject: string, draftBody: string, options) => {
                // WhatsApp — and a page-only web reply — carry no subject
                // line, so a draft's subject is dropped there rather than
                // typed into a field that does not exist.
                if (!noSubject && draftSubject.trim() !== '') setSubject(draftSubject)
                if (options?.rememberPrevious) {
                    setPreviousDraft(body.trim() !== '' ? body : null)
                }
                setDraftTriageUid(options?.triageUid ?? null)
                setBody(draftBody)
                setError(null)
                textareaRef.current?.focus()
            },
            insertText: (text: string) => {
                setBody((prev) => (prev.trim() ? `${prev.trimEnd()}\n\n${text}` : text))
                textareaRef.current?.focus()
            },
        }

        return () => {
            apiRef.current = null
        }
    }, [apiRef, body, noSubject, textareaRef])

    const canSend = !reply.isPending && !unreachable && (templateMode ? templateReady : body.trim() !== '')
    const handleBodyKeyDown = useSendOnEnter({ send: submit, enabled: canSend })
    const isCoarsePointer = useCoarsePointer()

    return (
        <section className="border-t px-4 md:px-5 py-3 space-y-2 bg-card" data-testid="conversation-reply-card">
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                <span className="truncate">
                    <span className="font-medium text-foreground">{t('panel.reply_to')}</span> {recipient}
                </span>
            </div>
            {unreachable && (
                <p className="text-xs text-muted-foreground" data-testid="conversation-reply-unreachable">
                    {isWhatsApp ? t('whatsapp.unreachable') : t('errors.no_recipient')}
                </p>
            )}
            {pageOnly && (
                <p className="text-xs text-muted-foreground" data-testid="conversation-reply-page-only">
                    {t('composer.page_only')}{' '}
                    <button
                        type="button"
                        className="underline hover:text-foreground"
                        onClick={onAttachMedium}
                        data-testid="conversation-reply-attach-medium"
                    >
                        {t('contact.attach_medium')}
                    </button>
                </p>
            )}
            {isWhatsApp && !unreachable && (
                <p
                    className={`text-xs flex items-start gap-1.5 ${windowOpen ? 'text-muted-foreground' : 'text-amber-700 dark:text-amber-400'}`}
                    data-testid={windowOpen ? 'conversation-whatsapp-window-open' : 'conversation-whatsapp-window-closed'}
                >
                    <AppIcon path={mdiCellphone} size="xs" className="mt-0.5 flex-shrink-0" aria-hidden />
                    <span>
                        {windowOpen && windowUntil
                            ? t('whatsapp.window_open', { time: formatDate(windowUntil.toISOString(), { dateStyle: 'short', timeStyle: 'short' }) })
                            : t('whatsapp.window_closed')}
                        {windowOpen && (
                            <>
                                {' '}
                                <button
                                    type="button"
                                    className="underline hover:text-foreground"
                                    onClick={() => {
                                        setUseTemplate(!useTemplate)
                                        setError(null)
                                    }}
                                    data-testid="conversation-template-toggle"
                                >
                                    {useTemplate ? t('template.back_to_text') : t('template.send_instead')}
                                </button>
                            </>
                        )}
                    </span>
                </p>
            )}
            {templateMode && (
                <WhatsAppTemplatePicker
                    templates={catalogue.data?.templates ?? []}
                    defaultLanguage={catalogue.data?.default_language}
                    warning={catalogue.data?.warning}
                    continueTemplate={continueName}
                    loading={catalogue.isLoading}
                    selected={template}
                    onSelect={(tpl) => {
                        setTemplate(tpl)
                        setError(null)
                    }}
                    params={templateParams}
                    onParamsChange={setTemplateParams}
                    disabled={reply.isPending}
                />
            )}
            {!noSubject && (
                <Input
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    onKeyDown={(e) => {
                        // A single-line field: Enter means "next", not
                        // "send" — move on to the body rather than doing
                        // nothing.
                        if (e.key === 'Enter') {
                            e.preventDefault()
                            textareaRef.current?.focus()
                        }
                    }}
                    onBlur={draft.flushNow}
                    placeholder={t('actions.reply_subject')}
                    aria-label={t('actions.reply_subject')}
                    className="h-8 text-sm"
                    disabled={unreachable}
                    data-testid="conversation-reply-subject"
                />
            )}
            {(!templateMode || template?.has_url_button) && (
                <Textarea
                    ref={textareaRef}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={handleBodyKeyDown}
                    onBlur={draft.flushNow}
                    placeholder={templateMode ? t('template.page_message') : t('actions.reply_body')}
                    aria-label={templateMode ? t('template.page_message') : t('actions.reply_body')}
                    rows={templateMode ? 2 : 4}
                    className="text-sm"
                    disabled={unreachable}
                    data-testid="conversation-reply-body"
                />
            )}
            {canShareDocs && (
                <ComposerDocuments
                    org={org}
                    documents={sharedDocs}
                    disabled={reply.isPending}
                    onAdd={(doc) => {
                        setSharedDocs((prev) => (prev.some((d) => d.uid === doc.uid) ? prev : [...prev, doc]))
                        setError(null)
                    }}
                    onRemove={(uid) => setSharedDocs((prev) => prev.filter((d) => d.uid !== uid))}
                />
            )}
            <ConfirmInternalDocumentsDialog
                open={confirmingInternal}
                names={(sharedDocs.some((d) => d.internal) ? sharedDocs.filter((d) => d.internal) : sharedDocs).map((d) => d.name)}
                onCancel={() => setConfirmingInternal(false)}
                onConfirm={() => {
                    setConfirmingInternal(false)
                    sendReply(true)
                }}
            />
            {error && (
                <p className="text-xs text-destructive" data-testid="conversation-reply-error">
                    {error}
                </p>
            )}
            {previousDraft !== null && (
                <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-2" data-testid="composer-previous-draft">
                    <span>{t('composer.previous_draft')}</span>
                    <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => {
                            setBody(previousDraft)
                            setPreviousDraft(null)
                            textareaRef.current?.focus()
                        }}
                        data-testid="composer-previous-draft-show"
                    >
                        {t('composer.previous_draft_show')}
                    </button>
                    <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => setPreviousDraft(null)}
                        data-testid="composer-previous-draft-delete"
                    >
                        {t('composer.previous_draft_delete')}
                    </button>
                </p>
            )}
            <div className="flex items-center justify-end gap-2">
                {draft.status !== 'idle' && (
                    <span className="text-xs text-muted-foreground" data-testid="conversation-draft-status">
                        {draft.status === 'saving'
                            ? t('composer.draft_saving')
                            : t('composer.draft_saved', { time: formatDate((draft.savedAt ?? new Date()).toISOString(), { timeStyle: 'short' }) })}
                    </span>
                )}
                {!isCoarsePointer && (
                    <span className="text-xs text-muted-foreground" data-testid="conversation-composer-hint">
                        {t('composer.hint')}
                    </span>
                )}
                <Button
                    size="sm"
                    disabled={reply.isPending || unreachable || (templateMode && !templateReady)}
                    onClick={submit}
                    aria-label={t('actions.reply')}
                    title={t('actions.reply')}
                    data-testid="conversation-send-reply"
                >
                    <AppIcon path={mdiSend} size="xs" className="md:mr-1" />
                    <span className="hidden md:inline">{t('actions.reply')}</span>
                </Button>
            </div>
        </section>
    )
}

/* ------------------------------------------------------------------------ */
/* Details: contact                                                          */
/* ------------------------------------------------------------------------ */

function ContactSection({
    org,
    conversation,
    onChanged,
    openMediumSignal = 0,
}: {
    org: string
    conversation: Conversation
    onChanged: () => void
    /** Bumped by the composer's page-only hint to open the medium step. */
    openMediumSignal?: number
}) {
    const { t } = useTranslation('conversations')
    const [attaching, setAttaching] = useState(false)
    // The medium step of « Rattacher une adresse » (spec 2026-09-14-03 § B).
    // `pendingContactUid` is the contact the picker just produced but that is
    // not attached yet: the switch attaches it in the same call, so the two
    // gestures are one round trip.
    const [pickingForMedium, setPickingForMedium] = useState(false)
    const [choosingMedium, setChoosingMedium] = useState(false)
    const [pendingContactUid, setPendingContactUid] = useState<string | null>(null)
    const patch = $api.useMutation('patch', '/api/v1/organizations/{orgCode}/conversations/{conversationUid}', {
        onSuccess: onChanged,
    })

    const resolution = conversation.contact_resolution ?? 'unresolved'
    const contactName = conversation.contact?.name ? String(conversation.contact.name) : null
    const contactUid = conversation.contact?.uid ? String(conversation.contact.uid) : null
    const identity = conversation.channel_identity ?? ''
    // Only a `web` thread can acquire a medium — the server refuses anything
    // else with a 409, so the control is not offered elsewhere.
    const canAttachMedium = conversation.channel === 'web'

    // Opening from the composer's hint: pick the contact first when there is
    // none, because a medium with nobody behind it enriches nothing.
    const startMedium = useCallback(() => {
        if (contactUid) {
            setPendingContactUid(null)
            setChoosingMedium(true)

            return
        }
        setPickingForMedium(true)
    }, [contactUid])

    useEffect(() => {
        if (openMediumSignal > 0 && canAttachMedium) startMedium()
    }, [openMediumSignal, canAttachMedium, startMedium])

    return (
        <DetailSection sectionKey="contact" title={t('panel.contact')} testId="conversation-contact-section">
            <div className="flex items-start gap-2">
                <AppIcon path={mdiAccountOutline} size="sm" className="mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                    <div className="text-sm text-foreground truncate" data-testid="conversation-contact">
                        {contactUid ? (
                            <Link to="/$org/users/$userUid" params={{ org, userUid: contactUid }} className="hover:underline">
                                {contactName ?? identity}
                            </Link>
                        ) : (
                            contactName ?? displaySender(conversation, t('contact.anonymous'))
                        )}
                    </div>
                    {identity && !identity.startsWith('anonymous:') && (
                        <div className="text-[11px] text-muted-foreground truncate">{identity}</div>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        <Badge variant={resolution === 'unresolved' ? 'destructive' : 'secondary'} className="text-[10px]">
                            {t(`resolution.${resolution}`, { defaultValue: resolution })}
                        </Badge>
                        {conversation.contact_role && (
                            <span className="text-[11px] text-muted-foreground">
                                {t('contact.role', { value: conversation.contact_role })}
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {attaching || pickingForMedium ? (
                <div className="space-y-1">
                    <ContactPicker
                        org={org}
                        onChange={(userUid) => {
                            // Picked FOR the medium step: the contact is not
                            // attached here — the switch attaches it with the
                            // address, in one call.
                            if (pickingForMedium) {
                                setPendingContactUid(userUid)
                                setPickingForMedium(false)
                                setChoosingMedium(true)

                                return
                            }
                            patch.mutate({
                                params: { path: { orgCode: org, conversationUid: conversation.uid! } },
                                body: { contact_user_uid: userUid },
                            })
                            setAttaching(false)
                        }}
                    />
                    <p className="text-[11px] text-muted-foreground">{t('contact.attach_hint')}</p>
                </div>
            ) : choosingMedium ? (
                <MediumStep
                    org={org}
                    conversation={conversation}
                    contactUserUid={pendingContactUid}
                    onDone={() => {
                        setChoosingMedium(false)
                        setPendingContactUid(null)
                        onChanged()
                    }}
                    onCancel={() => {
                        setChoosingMedium(false)
                        setPendingContactUid(null)
                    }}
                />
            ) : (
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setAttaching(true)}
                        data-testid="conversation-attach-contact"
                    >
                        {contactUid ? t('contact.change') : t('contact.attach')}
                    </Button>
                    {canAttachMedium && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={startMedium}
                            data-testid="conversation-attach-medium"
                        >
                            {t('contact.attach_medium')}
                        </Button>
                    )}
                    {contactUid && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() =>
                                patch.mutate({
                                    params: { path: { orgCode: org, conversationUid: conversation.uid! } },
                                    body: { contact_user_uid: '' },
                                })
                            }
                            data-testid="conversation-detach-contact"
                        >
                            {t('contact.detach')}
                        </Button>
                    )}
                </div>
            )}
        </DetailSection>
    )
}

/* ------------------------------------------------------------------------ */
/* Details: the medium step — "answer this thread where?"                    */
/* ------------------------------------------------------------------------ */

/**
 * Moves a `web` thread onto the contact's e-mail or WhatsApp (spec
 * 2026-09-14-03 § B).
 *
 * The choices are the identities the referential already holds — `users.email`,
 * the `phone` / `mobile_phone` properties and the contact's
 * `channel_identities` rows, served by `GET …/conversations/{uid}/channel` —
 * because retyping an address the platform already knows is how a wrong one
 * gets stored. "Autre adresse…" is the escape hatch, with an explicit channel
 * toggle: the same string can be neither, and guessing is not an option.
 */
function MediumStep({
    org,
    conversation,
    contactUserUid,
    onDone,
    onCancel,
}: {
    org: string
    conversation: Conversation
    /** The contact the picker just produced, attached by the switch itself. */
    contactUserUid: string | null
    onDone: () => void
    onCancel: () => void
}) {
    const { t } = useTranslation('conversations')
    const [manual, setManual] = useState(false)
    const [manualChannel, setManualChannel] = useState<'email' | 'whatsapp'>('email')
    const [manualIdentity, setManualIdentity] = useState('')
    const [error, setError] = useState<string | null>(null)

    const { data } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/channel', {
        params: {
            path: { orgCode: org, conversationUid: conversation.uid! },
            query: contactUserUid ? { contact_user_uid: contactUserUid } : {},
        },
    })
    const candidates = data?.candidates ?? []

    const switchChannel = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/channel',
        {
            onSuccess: onDone,
            onError: (res: unknown) => {
                const body = res as { title?: string; code?: string } | undefined
                setError(
                    body?.code === 'WHATSAPP_UNAVAILABLE'
                        ? t('errors.whatsapp_unavailable')
                        : (body?.title ?? t('contact.medium_error')),
                )
            },
        },
    )

    const apply = (channel: string, identity: string) => {
        setError(null)
        switchChannel.mutate({
            params: { path: { orgCode: org, conversationUid: conversation.uid! } },
            body: {
                channel: channel as 'email' | 'whatsapp',
                identity,
                ...(contactUserUid ? { contact_user_uid: contactUserUid } : {}),
            },
        })
    }

    return (
        <div className="space-y-1.5" data-testid="conversation-medium-step">
            <p className="text-[11px] text-muted-foreground">{t('contact.medium_title')}</p>
            {candidates.length > 0 && (
                <ul className="border rounded-md divide-y">
                    {candidates.map((candidate) => {
                        const candidateIcon = CHANNEL_ICON[candidate.channel as keyof typeof CHANNEL_ICON] ?? mdiMessageOutline

                        return (
                            <li key={`${candidate.channel}-${candidate.identity}`}>
                                <button
                                    type="button"
                                    className="w-full flex items-center gap-2 text-left px-2 py-1.5 text-xs hover:bg-muted/60"
                                    onClick={() => apply(candidate.channel, candidate.identity)}
                                    disabled={switchChannel.isPending}
                                    data-testid={`conversation-medium-option-${candidate.identity}`}
                                >
                                    <AppIcon path={candidateIcon} size="xs" className="flex-shrink-0" aria-hidden />
                                    <span className="truncate text-foreground">{candidate.identity}</span>
                                    <span className="ml-auto text-[10px] text-muted-foreground">
                                        {t(`contact.medium_source_${candidate.source}`, { defaultValue: candidate.source })}
                                    </span>
                                </button>
                            </li>
                        )
                    })}
                </ul>
            )}
            {candidates.length === 0 && !manual && (
                <EmptyState
                    variant="inline"
                    className="py-3"
                    description={t('contact.medium_none')}
                    data-testid="conversation-medium-empty"
                />
            )}
            {manual ? (
                <div className="space-y-1.5">
                    <Select value={manualChannel} onValueChange={(v) => setManualChannel(v as 'email' | 'whatsapp')}>
                        <SelectTrigger className="h-8 text-xs" data-testid="conversation-medium-channel">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="email">{t('channel.email')}</SelectItem>
                            <SelectItem value="whatsapp">{t('channel.whatsapp')}</SelectItem>
                        </SelectContent>
                    </Select>
                    <Input
                        value={manualIdentity}
                        onChange={(e) => setManualIdentity(e.target.value)}
                        placeholder={t(`contact.medium_placeholder_${manualChannel}`)}
                        aria-label={t(`contact.medium_placeholder_${manualChannel}`)}
                        className="h-8 text-sm"
                        data-testid="conversation-medium-identity"
                    />
                </div>
            ) : null}
            {error && (
                <p className="text-[11px] text-destructive" data-testid="conversation-medium-error">
                    {error}
                </p>
            )}
            <p className="text-[11px] text-muted-foreground">{t('contact.medium_hint')}</p>
            <div className="flex flex-wrap gap-2">
                {manual ? (
                    <Button
                        size="sm"
                        className="h-7 text-xs"
                        disabled={switchChannel.isPending || manualIdentity.trim() === ''}
                        onClick={() => apply(manualChannel, manualIdentity.trim())}
                        data-testid="conversation-medium-confirm"
                    >
                        {t('contact.medium_confirm')}
                    </Button>
                ) : (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setManual(true)}
                        data-testid="conversation-medium-other"
                    >
                        {t('contact.medium_other')}
                    </Button>
                )}
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={onCancel}
                    data-testid="conversation-medium-cancel"
                >
                    {t('contact.medium_cancel')}
                </Button>
            </div>
        </div>
    )
}

/* ------------------------------------------------------------------------ */
/* Details: assets                                                           */
/* ------------------------------------------------------------------------ */

function AssetsSection({ org, conversation, onChanged }: { org: string; conversation: Conversation; onChanged: () => void }) {
    const { t } = useTranslation('conversations')
    const [adding, setAdding] = useState(false)
    const linkAsset = $api.useMutation('post', '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/assets', {
        onSuccess: onChanged,
    })
    const unlinkAsset = $api.useMutation(
        'delete',
        '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/assets/{assetUid}',
        { onSuccess: onChanged },
    )

    const anchor = conversation.asset
    const others = (conversation.linked_assets ?? []).filter((link) => link.asset && link.asset.uid !== anchor?.uid)
    const none = !anchor && others.length === 0

    return (
        <DetailSection sectionKey="assets" title={t('panel.linked_assets')} testId="conversation-assets-section">
            <div className="space-y-1.5" data-testid="conversation-linked-assets">
                {anchor && (
                    <AssetRow
                        org={org}
                        uid={String(anchor.uid)}
                        name={String(anchor.name ?? '')}
                        anchor
                        anchorLabel={t('panel.anchor')}
                    />
                )}
                {others.map((link) => (
                    <AssetRow
                        key={link.uid}
                        org={org}
                        uid={String(link.asset?.uid)}
                        name={String(link.asset?.name ?? '')}
                        onRemove={() =>
                            unlinkAsset.mutate({
                                params: {
                                    path: { orgCode: org, conversationUid: conversation.uid!, assetUid: String(link.asset?.uid) },
                                },
                            })
                        }
                        removeLabel={t('actions.remove_asset')}
                    />
                ))}
                {/* "No asset yet" only when there is genuinely none — an
                    attached asset becomes the home of a homeless thread. */}
                {none && (
                    <p className="text-[11px] text-muted-foreground" data-testid="conversation-no-asset">
                        {t(`asset_resolution.${conversation.asset_resolution ?? 'unresolved'}`, {
                            defaultValue: t('asset_resolution.unresolved'),
                        })}
                    </p>
                )}
            </div>
            {adding ? (
                <AssetPicker
                    org={org}
                    testId="conversation-asset-picker"
                    scopeUid={anchor?.uid ? String(anchor.uid) : undefined}
                    scopeLabel={anchor?.name ? String(anchor.name) : undefined}
                    help={t('panel.attach_asset_help')}
                    onChange={(assetUid) => {
                        linkAsset.mutate({
                            params: { path: { orgCode: org, conversationUid: conversation.uid! } },
                            body: { asset_uid: assetUid },
                        })
                        setAdding(false)
                    }}
                />
            ) : (
                <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setAdding(true)}
                    data-testid="conversation-add-asset"
                >
                    <AppIcon path={mdiPlus} size="xs" className="mr-1" />
                    {t('actions.attach_asset')}
                </Button>
            )}
        </DetailSection>
    )
}

function AssetRow({
    org,
    uid,
    name,
    anchor = false,
    anchorLabel,
    onRemove,
    removeLabel,
}: {
    org: string
    uid: string
    name: string
    anchor?: boolean
    anchorLabel?: string
    onRemove?: () => void
    removeLabel?: string
}) {
    return (
        <div className="flex items-center gap-2 min-w-0">
            {anchor ? (
                <AppIcon path={mdiHome} size="xs" className="text-primary flex-shrink-0" aria-label={anchorLabel} />
            ) : (
                <AppIcon path={SECTION_ICONS.assets} size="xs" className="flex-shrink-0" />
            )}
            <Link
                to="/$org/assets/$assetUid"
                params={{ org, assetUid: uid }}
                className="text-sm text-foreground hover:underline truncate min-w-0 flex-1"
                title={anchor ? anchorLabel : undefined}
            >
                {name}
            </Link>
            {anchor && anchorLabel && (
                <Badge variant="outline" className="text-[10px] flex-shrink-0">
                    {anchorLabel}
                </Badge>
            )}
            {onRemove && (
                <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive flex-shrink-0"
                    aria-label={removeLabel}
                    title={removeLabel}
                    onClick={onRemove}
                >
                    <AppIcon path={mdiTrashCanOutline} size="xs" />
                </button>
            )}
        </div>
    )
}

/* ------------------------------------------------------------------------ */
/* Details: tickets                                                          */
/* ------------------------------------------------------------------------ */

function TicketsSection({
    org,
    conversation,
    onOpenTicket,
}: {
    org: string
    conversation: Conversation
    onOpenTicket: () => void
}) {
    const { t } = useTranslation('conversations')
    const links = conversation.linked_issues ?? []

    return (
        <DetailSection
            sectionKey="tickets"
            title={t('panel.linked_issues')}
            testId="conversation-tickets-section"
            trailing={
                <button
                    type="button"
                    className="text-[11px] text-primary hover:underline flex-shrink-0"
                    onClick={onOpenTicket}
                    data-testid="conversation-tickets-open"
                >
                    {t('actions.create_ticket')}
                </button>
            }
        >
            {links.length === 0 ? (
                <EmptyState variant="inline" className="py-3" description={t('panel.no_linked_issue')} />
            ) : (
                <ul className="space-y-1.5" data-testid="conversation-linked-issues">
                    {links.map((link) => (
                        <IssueRow key={link.uid} org={org} issue={(link.issue ?? {}) as LooseIssue} />
                    ))}
                </ul>
            )}
        </DetailSection>
    )
}

function IssueRow({ org, issue }: { org: string; issue: LooseIssue }) {
    const { t, i18n } = useTranslation('conversations')
    const status = issue.status ?? 'open'
    // The chip carries the reference the acknowledgement cited, so a manager
    // reading the thread sees the same number the resident was given.
    const reference = formatIssueReference(issue.number, i18n.language)

    return (
        <li className="rounded-md border bg-card px-2.5 py-2" data-testid={`conversation-issue-${issue.uid ?? ''}`}>
            <Link to="/$org/issues/$uid" params={{ org, uid: String(issue.uid ?? '') }} className="block min-w-0 group">
                <div className="flex items-center gap-2 min-w-0">
                    <AppIcon path={mdiTicketOutline} size="xs" className="flex-shrink-0" />
                    <span className="text-sm text-foreground truncate flex-1 group-hover:underline">
                        {reference && (
                            <span className="text-muted-foreground tabular-nums mr-1.5" data-testid="conversation-issue-number">
                                {reference}
                            </span>
                        )}
                        {issue.title || String(issue.uid ?? '')}
                    </span>
                    <AppIcon path={mdiOpenInNew} size="xs" className="flex-shrink-0 opacity-0 group-hover:opacity-100" />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className={`text-[10px] ${ISSUE_STATUS_TONE[status] ?? 'text-muted-foreground'}`}>
                        {t(`issue_status.${status}`, { defaultValue: status })}
                    </Badge>
                    {issue.subtype && (
                        <span className="text-[11px] text-muted-foreground">
                            {t(`promote.subtype_${issue.subtype}`, { defaultValue: issue.subtype })}
                        </span>
                    )}
                    {issue.due_date && <span className="text-[11px] text-muted-foreground">· {issue.due_date}</span>}
                </div>
            </Link>
        </li>
    )
}

/* ------------------------------------------------------------------------ */
/* Details: context                                                          */
/* ------------------------------------------------------------------------ */

/**
 * The context is what the referential already knows about the asset this
 * request is about: the asset itself, the open tickets on it, the other
 * requests about it. Deterministic and sourced.
 *
 * It no longer carries a "the triage job will bring more" hint: the retrieved
 * facts and the suggested answer arrived (spec 2026-09-11-06) and they render in
 * the Triage card above this one. This section is what a manager reads when
 * there is no proposal — which is most threads on a deployment with no LLM.
 */
function ContextSection({ org, conversation }: { org: string; conversation: Conversation }) {
    const { t } = useTranslation('conversations')
    const assetUid = conversation.asset?.uid ? String(conversation.asset.uid) : ''

    const asset = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}',
        { params: { path: { orgCode: org, assetUid }, query: { include: ['picture'] } } },
        { enabled: assetUid !== '' },
    )
    // The address is a property value, not a column, so it takes the property
    // endpoint — which already embeds each definition, hence its `code`.
    const assetProperties = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}/properties',
        { params: { path: { orgCode: org, assetUid } } },
        { enabled: assetUid !== '' },
    )
    const issues = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/issues',
        { params: { path: { orgCode: org }, query: { asset_uid: assetUid, status: 'open,in_progress', limit: 5 } } },
        { enabled: assetUid !== '' },
    )
    const threads = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/conversations',
        {
            params: {
                path: { orgCode: org },
                query: { asset: assetUid, status: 'open,waiting_resident,closed', limit: 6 },
            },
        },
        { enabled: assetUid !== '' },
    )

    const assetInfo = (asset.data ?? {}) as Record<string, unknown>
    const assetType = (assetInfo.asset_type as { name?: Record<string, string> } | undefined)?.name
    const typeLabel = assetType ? assetType.fr || assetType.en || Object.values(assetType)[0] : ''
    const openIssues = (((issues.data as { data?: LooseIssue[] } | undefined)?.data ?? []) as LooseIssue[]).filter(
        (i) => !(conversation.linked_issues ?? []).some((l) => String(l.issue?.uid) === String(i.uid)),
    )
    const otherThreads = (((threads.data as { data?: Conversation[] } | undefined)?.data ?? []) as Conversation[]).filter(
        (c) => c.uid !== conversation.uid,
    )

    // The address, off the asset's own property values. Absent on a floor or a
    // unit, which is normal — the card just does not print the line.
    const address = useMemo(() => {
        const values = ((assetProperties.data as { data?: unknown[] } | undefined)?.data ?? []) as {
            value?: unknown
            property_definition?: { code?: string }
        }[]
        const byCode = (code: string) => values.find((entry) => entry.property_definition?.code === code)?.value
        const iso = byCode('address_iso20022')
        if (iso && typeof iso === 'object') {
            const formatted = formatIso20022AddressShort(iso as Record<string, unknown>)
            if (formatted) return formatted
        }
        const raw = byCode('address_raw')

        return typeof raw === 'string' && raw.trim() ? raw : ''
    }, [assetProperties.data])
    const pictureUid = (assetInfo.picture as { document_uid?: string } | null | undefined)?.document_uid

    return (
        <DetailSection sectionKey="context" title={t('panel.context')} testId="conversation-context-card">
            {!assetUid ? (
                <EmptyState variant="inline" className="py-3" description={t('panel.context_empty')} />
            ) : (
                <div className="space-y-3">
                    {/* The asset card leads: "what the referential knows" starts
                        with which building this is, and the operator should not
                        have to look it up in the Assets section above. */}
                    <div className="rounded-md border bg-card p-2.5 flex items-start gap-2.5" data-testid="conversation-context-asset-card">
                        {pictureUid && (
                            <EntityPicture
                                entityType="asset"
                                entityId={assetUid}
                                orgCode={org}
                                size="100x100"
                                documentUid={pictureUid}
                                editable={false}
                                className="h-12 w-12 rounded flex-shrink-0 object-cover"
                                alt={String(assetInfo.name ?? '')}
                            />
                        )}
                        <div className="min-w-0 flex-1">
                            <div className="text-[11px] text-muted-foreground">{t('panel.context_asset')}</div>
                            <Link
                                to="/$org/assets/$assetUid"
                                params={{ org, assetUid }}
                                className="text-sm text-foreground hover:underline block truncate"
                                data-testid="conversation-context-asset-link"
                            >
                                {String(assetInfo.name ?? conversation.asset?.name ?? '')}
                            </Link>
                            {typeLabel && <div className="text-[11px] text-muted-foreground truncate">{typeLabel}</div>}
                            {address && (
                                <div
                                    className="mt-0.5 flex items-start gap-1 text-[11px] text-muted-foreground"
                                    data-testid="conversation-context-asset-address"
                                >
                                    <AppIcon path={mdiMapMarker} size="xs" className="mt-0.5 flex-shrink-0" aria-hidden />
                                    <span className="min-w-0">{address}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-1">
                        <div className="text-[11px] text-muted-foreground">
                            {t('panel.context_open_tickets', { count: openIssues.length })}
                        </div>
                        {openIssues.length > 0 && (
                            <ul className="space-y-1">
                                {openIssues.slice(0, 5).map((issue) => (
                                    <IssueRow key={String(issue.uid)} org={org} issue={issue} />
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="space-y-1">
                        <div className="text-[11px] text-muted-foreground">
                            {t('panel.context_other_threads', { count: otherThreads.length })}
                        </div>
                        {otherThreads.length > 0 && (
                            <ul className="space-y-1">
                                {otherThreads.slice(0, 5).map((row) => (
                                    <li key={row.uid} className="text-xs min-w-0">
                                        <Link
                                            to="/$org/conversations"
                                            params={{ org }}
                                            search={{
                                                conversation: row.uid,
                                                status: 'open,waiting_resident,closed',
                                                channel: undefined,
                                                resolution: undefined,
                                                asset: undefined,
                                                unattached: undefined,
                                                draft: undefined,
                                                q: undefined,
                                                selected: undefined,
                                                reply_for_issue: undefined,
                                                message: undefined,
                                            }}
                                            className="block truncate text-foreground hover:underline"
                                        >
                                            {threadTitleT(row, t)}
                                        </Link>
                                        <span className="block truncate text-[11px] text-muted-foreground">
                                            {displaySender(row, t('contact.anonymous'))}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                </div>
            )}
        </DetailSection>
    )
}

/* ------------------------------------------------------------------------ */
/* Details: tags                                                             */
/* ------------------------------------------------------------------------ */

function TagsSection({ org, conversation }: { org: string; conversation: Conversation }) {
    const { t } = useTranslation('conversations')

    return (
        <DetailSection sectionKey="tags" title={t('panel.tags')} testId="conversation-tags-section">
            <EntityTagsSection
                org={org}
                entityType="conversation"
                entityUid={conversation.uid ?? ''}
                className="flex flex-wrap items-center gap-1"
            />
        </DetailSection>
    )
}

/* ------------------------------------------------------------------------ */
/* Promote                                                                   */
/* ------------------------------------------------------------------------ */

/**
 * One request candidate, confirmed by a human. Nothing is created without a click.
 *
 * Two entries, one form: the plain *Open a ticket* gesture, and the triage
 * card's *Edit*, which arrives with a `seed`. The difference is only where the
 * submit goes — a seeded submit REVIEWS the proposal as `edited`, so the
 * acceptance history records "a human had to correct it" rather than "a human
 * opened a ticket by hand" (spec 2026-09-11-06 § 2). Anything else would make
 * the acceptance rate unreadable.
 */
function PromoteForm({
    org,
    conversation,
    seed,
    onDone,
    onCancel,
}: {
    org: string
    conversation: Conversation
    seed?: PromoteSeed | null
    onDone: () => void
    onCancel: () => void
}) {
    const { t } = useTranslation('conversations')
    const firstInbound = (conversation.messages ?? []).find((m) => m.direction === 'inbound')
    const [title, setTitle] = useState(seed?.title || threadTitle(conversation, ''))
    const [subtype, setSubtype] = useState<RequestSubtype>(
        (seed?.subtype as RequestSubtype) || 'request_intervention',
    )
    // The ticket's asset defaults to the thread's anchor, but it is a choice,
    // not a hidden field: the anchor may be missing (an unresolved sender, an
    // asset since deleted), and a ticket on the wrong asset is worse than one
    // with none.
    const [assetUid, setAssetUid] = useState(
        seed?.assetUid || (conversation.asset?.uid ? String(conversation.asset.uid) : ''),
    )
    const [assetName, setAssetName] = useState(
        seed?.assetName || (conversation.asset?.name ? String(conversation.asset.name) : ''),
    )
    const [error, setError] = useState<string | null>(null)

    const onError = (e: unknown) => {
        const body = e as { title?: string } | undefined
        setError(body?.title ? `${t('promote.error')} ${body.title}` : t('promote.error'))
    }
    const promote = $api.useMutation('post', '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/promote', {
        onSuccess: onDone,
        onError,
    })
    const reviewEdited = $api.useMutation(
        'post',
        '/api/v1/organizations/{orgCode}/conversations/{conversationUid}/messages/{messageUid}/triage/review',
        { onSuccess: onDone, onError },
    )
    const pending = promote.isPending || reviewEdited.isPending

    const submit = () => {
        const request = {
            title,
            description: firstInbound?.body_text ?? '',
            subtype,
            asset_uid: assetUid || undefined,
            message_uid: seed?.reviewMessageUid ?? firstInbound?.uid,
        }
        if (seed) {
            reviewEdited.mutate({
                params: {
                    path: { orgCode: org, conversationUid: conversation.uid!, messageUid: seed.reviewMessageUid },
                },
                body: { decision: 'edited', requests: [request] },
            } as never)

            return
        }
        promote.mutate({
            params: { path: { orgCode: org, conversationUid: conversation.uid! } },
            body: { requests: [request] },
        } as never)
    }

    return (
        <div className="rounded-lg border bg-card p-3 space-y-2" data-testid="conversation-promote-form">
            <div className="text-xs font-medium text-foreground">
                {seed ? t('triage.edit_title') : t('promote.title')}
            </div>
            <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                aria-label={t('promote.ticket_title')}
                placeholder={t('promote.ticket_title')}
                className="h-8 text-sm"
                data-testid="promote-title"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <Select value={subtype} onValueChange={(v) => setSubtype(v as RequestSubtype)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="promote-subtype" aria-label={t('promote.subtype')}>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {REQUEST_SUBTYPES.map((code) => (
                            <SelectItem key={code} value={code}>
                                {t(`promote.subtype_${code}`)}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <AssetPicker
                    org={org}
                    value={assetUid || undefined}
                    valueLabel={assetName || undefined}
                    testId="promote-asset-picker"
                    scopeUid={conversation.asset?.uid ? String(conversation.asset.uid) : undefined}
                    scopeLabel={conversation.asset?.name ? String(conversation.asset.name) : undefined}
                    help={t('promote.asset_help')}
                    onChange={(uid, name, pathLabel) => {
                        setAssetUid(uid)
                        setAssetName(pathLabel || name)
                    }}
                />
            </div>
            {error && (
                <p className="text-xs text-destructive" data-testid="promote-error">
                    {error}
                </p>
            )}
            <div className="flex gap-2">
                <Button
                    size="sm"
                    disabled={pending || !title.trim()}
                    onClick={submit}
                    data-testid="promote-submit"
                >
                    <AppIcon path={mdiTicketOutline} size="xs" className="mr-1" />
                    {t('promote.submit')}
                </Button>
                <Button variant="outline" size="sm" onClick={onCancel} data-testid="promote-cancel">
                    {t('promote.cancel')}
                </Button>
            </div>
        </div>
    )
}
